/* eslint-disable no-unused-vars */
// BrowseJobsScreen.js
// Full-featured Job Browser — professional/candidate side
// Tabs: Browse · Saved · Applied · Alerts
// JobEng · professional screens

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Image,
  Dimensions, ScrollView, TextInput, Modal, BackHandler,
  ActivityIndicator, FlatList, RefreshControl, Alert, Share,
  Animated, Platform, Switch, KeyboardAvoidingView, StatusBar,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../../utils/apiClient';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// ─── Config ───────────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const { width, height } = Dimensions.get('window');
const CARD_W = (width - 48) / 2;

// ─── Constants ────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'browse',  label: 'Browse',  icon: 'briefcase-search-outline' },
  { id: 'saved',   label: 'Saved',   icon: 'bookmark-outline'         },
  { id: 'applied', label: 'Applied', icon: 'send-outline'             },
  { id: 'alerts',  label: 'Alerts',  icon: 'bell-outline'             },
];

const JOB_CATEGORIES = [
  { id: 'all',        name: 'All Jobs',    icon: 'briefcase-outline'      },
  { id: 'software',   name: 'Software',    icon: 'code-slash-outline'     },
  { id: 'mechanical', name: 'Mechanical',  icon: 'construct-outline'      },
  { id: 'electrical', name: 'Electrical',  icon: 'flash-outline'          },
  { id: 'civil',      name: 'Civil',       icon: 'business-outline'       },
  { id: 'chemical',   name: 'Chemical',    icon: 'flask-outline'          },
  { id: 'aerospace',  name: 'Aerospace',   icon: 'airplane-outline'       },
  { id: 'robotics',   name: 'Robotics',    icon: 'hardware-chip-outline'  },
  { id: 'data',       name: 'Data & AI',   icon: 'analytics-outline'      },
  { id: 'management', name: 'Management',  icon: 'people-outline'         },
  { id: 'design',     name: 'Design',      icon: 'color-palette-outline'  },
  { id: 'finance',    name: 'Finance',     icon: 'cash-outline'           },
];

const JOB_TYPES        = ['All Types','Full-time','Part-time','Contract','Remote','Hybrid','Internship','Freelance'];
const EXPERIENCE_LEVELS= ['All Levels','Entry Level','Mid Level','Senior','Lead','Director','Executive'];
const SALARY_RANGES    = [
  { label:'Any Salary', min:0,      max:999999 },
  { label:'£20k–£35k',  min:20000,  max:35000  },
  { label:'£35k–£50k',  min:35000,  max:50000  },
  { label:'£50k–£70k',  min:50000,  max:70000  },
  { label:'£70k–£90k',  min:70000,  max:90000  },
  { label:'£90k–£120k', min:90000,  max:120000 },
  { label:'£120k+',     min:120000, max:999999 },
];
const SORT_OPTIONS     = ['Best Match','Newest First','Oldest First','Highest Salary','Lowest Salary','Most Popular'];
const COMPANY_SIZES    = ['Any Size','1–10','11–50','51–200','201–500','500–1000','1000+'];
const DATE_POSTED      = ['Any Time','Last 24 hours','Last 3 days','Last week','Last month'];
const UK_LOCATIONS     = ['Any Location','London','Manchester','Birmingham','Leeds','Bristol','Edinburgh','Glasgow','Liverpool','Nottingham','Sheffield','Remote'];

const APP_STATUSES = {
  submitted:  { label:'Submitted',       color:'#3B82F6', icon:'send'              },
  reviewing:  { label:'Under Review',    color:'#F59E0B', icon:'eye-outline'       },
  shortlisted:{ label:'Shortlisted',     color:'#8B5CF6', icon:'account-check'     },
  interview:  { label:'Interview Scheduled',color:'#06B6D4',icon:'calendar-check'  },
  offered:    { label:'Offer Received',  color:'#10B981', icon:'party-popper'      },
  rejected:   { label:'Not Progressed',  color:'#EF4444', icon:'close-circle'      },
  withdrawn:  { label:'Withdrawn',       color:'#9CA3AF', icon:'exit-run'          },
};

const ALERT_FREQUENCIES = ['Instantly','Daily Digest','Weekly Summary'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatPosted = (d) => {
  if (!d) return 'Recently';
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return '1d ago';
  if (days < 7)   return `${days}d ago`;
  if (days < 30)  return `${Math.floor(days/7)}w ago`;
  return `${Math.floor(days/30)}mo ago`;
};
const formatSalary = (job) => {
  if (job.salary) return job.salary;
  const min = job.salaryMin || job.salary_min;
  const max = job.salaryMax || job.salary_max;
  const c   = job.currency || '£';
  if (min && max) return `${c}${(+min/1000).toFixed(0)}k–${c}${(+max/1000).toFixed(0)}k`;
  if (min)        return `From ${c}${(+min/1000).toFixed(0)}k`;
  return 'Competitive';
};
const parseSalaryNum = (job) => {
  const v = job.salaryMin || job.salary_min;
  if (v) return +v;
  if (job.salary) { const m = job.salary.replace(/,/g,'').match(/\d+/); return m ? +m[0] : 0; }
  return 0;
};
// Normalises a job's skills into a clean array of strings.
// Different companies store skills as ["React"], [{name:"React"}], or "React, Node",
// so we coerce every shape to a string array to avoid .toLowerCase() crashing.
const toSkillStrings = (job) => {
  let raw = job?.skills ?? job?.requiredSkills ?? [];
  if (typeof raw === 'string') raw = raw.split(',');
  if (!Array.isArray(raw)) return [];
  return raw
    .map(s => (typeof s === 'string' ? s : (s?.name || s?.title || s?.skill_name || s?.skill || '')))
    .map(s => String(s).trim())
    .filter(Boolean);
};
const getMatchScore = (job, userSkills = []) => {
  if (!userSkills.length) return null;
  const jSkills = toSkillStrings(job).map(s => s.toLowerCase());
  if (!jSkills.length) return null;
  const matched = userSkills.filter(s => jSkills.some(js => js.includes(s.toLowerCase())));
  return Math.round((matched.length / jSkills.length) * 100);
};
const matchColor = (s) => s >= 75 ? '#10B981' : s >= 50 ? '#F59E0B' : '#9CA3AF';
const getEmoji = (title = '') => {
  const t = title.toLowerCase();
  if (t.includes('software')||t.includes('developer')) return '💻';
  if (t.includes('mechanical')) return '⚙️';
  if (t.includes('electrical')) return '⚡';
  if (t.includes('civil')||t.includes('structural')) return '🏗️';
  if (t.includes('aerospace')) return '✈️';
  if (t.includes('chemical')) return '🧪';
  if (t.includes('data')||t.includes('ai')) return '📊';
  if (t.includes('design')) return '🎨';
  if (t.includes('manager')||t.includes('lead')) return '👔';
  if (t.includes('robot')||t.includes('automation')) return '🤖';
  if (t.includes('finance')||t.includes('account')) return '💰';
  return '🔧';
};
const daysLeft = (deadline) => {
  if (!deadline) return null;
  const d = Math.ceil((new Date(deadline).getTime() - Date.now()) / 86400000);
  return d > 0 ? d : 0;
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—';

// ─── Logo ─────────────────────────────────────────────────────────────────────
const JobengLogo = () => {
  try {
    const src = Image.resolveAssetSource(require('../../../assets/jobeng_icon.png'));
    if (src) return <Image source={require('../../../assets/jobeng_icon.png')} style={{ width:28, height:28 }} resizeMode="contain" />;
  } catch {}
  return <MaterialCommunityIcons name="briefcase" size={26} color="#FFF" />;
};

// ─── Component ────────────────────────────────────────────────────────────────
const BrowseJobsScreen = ({ navigation }) => {
  // Auth
  const [userId,    setUserId]    = useState(null);
  const [companyId, setCompanyId] = useState(null);

  // Data
  const [jobs,         setJobs]         = useState([]);
  const [savedJobsData,setSavedJobsData]= useState([]);  // full objects
  const [appliedJobs,  setAppliedJobs]  = useState([]);
  const [alerts,       setAlerts]       = useState([]);
  const [userSkills,   setUserSkills]   = useState([]);
  const [recentSearches,setRecentSearches]=useState([]);
  const [recentlyViewed,setRecentlyViewed]=useState([]);
  const [jobNotes,     setJobNotes]     = useState({}); // jobId -> note string

  // UI
  const [activeTab,    setActiveTab]    = useState('browse');
  const [viewMode,     setViewMode]     = useState('list');  // list | grid
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [searchQuery,  setSearchQuery]  = useState('');
  const [selectedCategory,setSelectedCategory]=useState('all');

  // Filters
  const [filterModal,      setFilterModal]      = useState(false);
  const [selectedJobType,  setSelectedJobType]  = useState('All Types');
  const [selectedExp,      setSelectedExp]      = useState('All Levels');
  const [selectedSalary,   setSelectedSalary]   = useState(0);
  const [selectedLocation, setSelectedLocation] = useState('Any Location');
  const [selectedCompSize, setSelectedCompSize] = useState('Any Size');
  const [selectedDatePost, setSelectedDatePost] = useState('Any Time');
  const [remoteOnly,       setRemoteOnly]       = useState(false);
  const [urgentOnly,       setUrgentOnly]       = useState(false);
  const [selectedSort,     setSelectedSort]     = useState('Best Match');
  const [filterCount,      setFilterCount]      = useState(0);

  // Selected / modals
  const [selectedJob,      setSelectedJob]      = useState(null);
  const [jobModal,         setJobModal]         = useState(false);
  const [compareJobs,      setCompareJobs]      = useState([]);
  const [compareMode,      setCompareMode]      = useState(false);
  const [compareModal,     setCompareModal]     = useState(false);
  const [prepModal,        setPrepModal]        = useState(false);
  const [prepQuestions,    setPrepQuestions]    = useState([]);
  const [noteModal,        setNoteModal]        = useState(false);
  const [noteDraft,        setNoteDraft]        = useState('');
  const [alertModal,       setAlertModal]       = useState(false);
  const [editAlert,        setEditAlert]        = useState(null);
  const [reportModal,      setReportModal]      = useState(false);
  const [reportReason,     setReportReason]     = useState('');
  const [salaryModal,      setSalaryModal]      = useState(false);

  // Alert form
  const [alertForm, setAlertForm] = useState({ title:'', keywords:'', location:'', jobType:'All Types', minSalary:'', frequency:'Daily Digest', active:true });

  // Sets
  const [savedIds,   setSavedIds]   = useState(new Set());
  const [appliedIds, setAppliedIds] = useState(new Set());

  // Misc
  const [applying,   setApplying]   = useState(false);
  const [savingAlert,setSavingAlert]= useState(false);
  const [generatingPrep,setGeneratingPrep]=useState(false);

  const fadeAnim  = useRef(new Animated.Value(0)).current;
  const searchRef = useRef(null);

  // ─── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    initScreen();
    const bh = BackHandler.addEventListener('hardwareBackPress', onBack);
    return () => bh.remove();
  }, []);

  useEffect(() => { recountFilters(); }, [selectedJobType,selectedExp,selectedSalary,selectedLocation,selectedCompSize,selectedDatePost,remoteOnly,urgentOnly]);

  const initScreen = async () => {
    const uid = await AsyncStorage.getItem('userId') || await AsyncStorage.getItem('user_id');
    const cid = await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('business_id');
    setUserId(uid); setCompanyId(cid);
    const rs = await AsyncStorage.getItem('recent_job_searches');
    if (rs) setRecentSearches(JSON.parse(rs));
    const rv = await AsyncStorage.getItem('recently_viewed_jobs');
    if (rv) setRecentlyViewed(JSON.parse(rv).slice(0,10));
    const notes = await AsyncStorage.getItem(`job_notes_${uid||'guest'}`);
    if (notes) setJobNotes(JSON.parse(notes));
    await Promise.all([
      loadJobs(), loadSavedJobs(uid), loadAppliedJobs(uid),
      loadUserSkills(uid), loadAlerts(uid),
    ]);
    Animated.timing(fadeAnim, { toValue:1, duration:400, useNativeDriver:true }).start();
  };

  const onBack = () => {
    if (filterModal)  { setFilterModal(false);  return true; }
    if (jobModal)     { setJobModal(false);      return true; }
    if (compareModal) { setCompareModal(false);  return true; }
    if (alertModal)   { setAlertModal(false);    return true; }
    if (prepModal)    { setPrepModal(false);     return true; }
    if (noteModal)    { setNoteModal(false);     return true; }
    if (reportModal)  { setReportModal(false);   return true; }
    if (salaryModal)  { setSalaryModal(false);   return true; }
    return false;
  };

  // ─── Data loaders ──────────────────────────────────────────────────────────
  // Uses shared fetchPublicJobs which has NO company_id filter — professionals
  // see every business's active jobs, not just one company's
  const loadJobs = async (isRefresh = false) => {
    if (!isRefresh) setLoading(true);
    try {
      const { fetchPublicJobs } = require('./jobsShared');
      const all = await fetchPublicJobs({ limit: 200 });
      if (all.length) {
        setJobs(all);
        await AsyncStorage.setItem('cached_browse_jobs', JSON.stringify(all));
      } else {
        const cached = await AsyncStorage.getItem('cached_browse_jobs');
        if (cached) setJobs(JSON.parse(cached));
      }
    } catch {
      const cached = await AsyncStorage.getItem('cached_browse_jobs');
      if (cached) setJobs(JSON.parse(cached));
    } finally { setLoading(false); setRefreshing(false); }
  };

  const loadSavedJobs = async (uid) => {
    try {
      const r = await apiFetch(`/api/professional/saved-jobs${uid?`?user_id=${uid}`:''}`);
      if (!r.error && (r.data||r.jobs)) {
        const data = r.data||r.jobs||[];
        setSavedJobsData(data);
        setSavedIds(new Set(data.map(j=>String(j.id||j.job_id))));
        await AsyncStorage.setItem(`saved_jobs_data_${uid||'g'}`, JSON.stringify(data));
        return;
      }
    } catch {}
    const cached = await AsyncStorage.getItem(`saved_jobs_data_${uid||'g'}`);
    if (cached) {
      const data = JSON.parse(cached);
      setSavedJobsData(data);
      setSavedIds(new Set(data.map(j=>String(j.id||j.job_id))));
    }
  };

  const loadAppliedJobs = async (uid) => {
    try {
      const r = await apiFetch(`/api/professional/applications${uid?`?user_id=${uid}`:''}`);
      if (!r.error && (r.data||r.applications)) {
        const data = r.data||r.applications||[];
        setAppliedJobs(data);
        setAppliedIds(new Set(data.map(a=>String(a.job_id||a.id))));
        await AsyncStorage.setItem(`applied_jobs_${uid||'g'}`, JSON.stringify(data));
        return;
      }
    } catch {}
    const cached = await AsyncStorage.getItem(`applied_jobs_${uid||'g'}`);
    if (cached) {
      const data = JSON.parse(cached);
      setAppliedJobs(data);
      setAppliedIds(new Set(data.map(a=>String(a.job_id||a.id))));
    }
  };

  const loadUserSkills = async (uid) => {
    if (!uid) return;
    const cached = await AsyncStorage.getItem(`user_skills_${uid}`);
    if (cached) setUserSkills(JSON.parse(cached));

    // Primary: try the unified service (matches what SkillProgress / Profile use)
    try {
      await ProfileManagementService.bootstrap();
      const svc = await ProfileManagementService.getProfile();
      if (svc.success && Array.isArray(svc.data?.skills) && svc.data.skills.length > 0) {
        const sk = svc.data.skills.map(s =>
          typeof s === 'string' ? s : (s?.name || s?.title || s?.skill_name)
        ).filter(Boolean);
        if (sk.length > 0) {
          setUserSkills(sk);
          await AsyncStorage.setItem(`user_skills_${uid}`, JSON.stringify(sk));
          return;
        }
      }
    } catch (_) {}

    // Fallback: direct skills endpoint
    const r = await apiFetch(`/api/users/${uid}/skills`);
    if (!r.error && r.data) {
      const sk = r.data.map(s=>s.skill_name||s.name||s);
      setUserSkills(sk);
      await AsyncStorage.setItem(`user_skills_${uid}`, JSON.stringify(sk));
    }
  };

  const loadAlerts = async (uid) => {
    if (!uid) return;
    const cached = await AsyncStorage.getItem(`job_alerts_${uid}`);
    if (cached) setAlerts(JSON.parse(cached));
    const r = await apiFetch(`/api/professional/alerts?user_id=${uid}`);
    if (!r.error && (r.data||r.alerts)) {
      const data = r.data||r.alerts||[];
      setAlerts(data);
      await AsyncStorage.setItem(`job_alerts_${uid}`, JSON.stringify(data));
    }
  };

  // ─── Derived: filtered jobs ────────────────────────────────────────────────
  const filteredJobs = useCallback(() => {
    let list = [...jobs];

    if (selectedCategory !== 'all') {
      const cat = selectedCategory.toLowerCase();
      list = list.filter(j => (j.title||'').toLowerCase().includes(cat)||(j.department||'').toLowerCase().includes(cat));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(j =>
        (j.title||'').toLowerCase().includes(q)||
        (j.company||j.department||'').toLowerCase().includes(q)||
        (j.location||'').toLowerCase().includes(q)||
        toSkillStrings(j).join(' ').toLowerCase().includes(q)||
        (j.description||'').toLowerCase().includes(q)
      );
    }
    if (selectedJobType !== 'All Types') {
      list = list.filter(j => (j.type||j.jobType||j.job_type||'').toLowerCase().includes(selectedJobType.toLowerCase()));
    }
    if (remoteOnly) {
      list = list.filter(j => (j.type||j.jobType||'').toLowerCase().includes('remote')||(j.location||'').toLowerCase().includes('remote'));
    }
    if (urgentOnly) {
      list = list.filter(j => {
        const dl = daysLeft(j.deadline||j.closingDate||j.closing_date);
        return dl !== null && dl <= 7;
      });
    }
    if (selectedExp !== 'All Levels') {
      list = list.filter(j => (j.description||j.requirements||j.experienceLevel||'').toLowerCase().includes(selectedExp.toLowerCase()));
    }
    if (selectedSalary !== 0) {
      const r = SALARY_RANGES[selectedSalary];
      list = list.filter(j => { const s=parseSalaryNum(j); return s>=r.min && s<=r.max; });
    }
    if (selectedLocation !== 'Any Location') {
      list = list.filter(j => (j.location||'').toLowerCase().includes(selectedLocation.toLowerCase()));
    }
    if (selectedDatePost !== 'Any Time') {
      const msMap = { 'Last 24 hours':86400000, 'Last 3 days':3*86400000, 'Last week':7*86400000, 'Last month':30*86400000 };
      const ms = msMap[selectedDatePost];
      if (ms) list = list.filter(j => (Date.now()-new Date(j.createdAt||j.created_at||0).getTime()) < ms);
    }

    switch (selectedSort) {
      case 'Best Match':      list.sort((a,b)=>(getMatchScore(b,userSkills)||0)-(getMatchScore(a,userSkills)||0)); break;
      case 'Newest First':    list.sort((a,b)=>new Date(b.createdAt||b.created_at||0)-new Date(a.createdAt||a.created_at||0)); break;
      case 'Oldest First':    list.sort((a,b)=>new Date(a.createdAt||a.created_at||0)-new Date(b.createdAt||b.created_at||0)); break;
      case 'Highest Salary':  list.sort((a,b)=>parseSalaryNum(b)-parseSalaryNum(a)); break;
      case 'Lowest Salary':   list.sort((a,b)=>parseSalaryNum(a)-parseSalaryNum(b)); break;
      case 'Most Popular':    list.sort((a,b)=>(b.applicants||b.views||0)-(a.applicants||a.views||0)); break;
    }
    return list;
  }, [jobs, searchQuery, selectedCategory, selectedJobType, selectedExp, selectedSalary, selectedLocation, selectedDatePost, remoteOnly, urgentOnly, selectedSort, userSkills]);

  const visibleJobs = filteredJobs();

  const recountFilters = () => {
    let c = 0;
    if (selectedJobType   !== 'All Types')    c++;
    if (selectedExp       !== 'All Levels')   c++;
    if (selectedSalary    !== 0)              c++;
    if (selectedLocation  !== 'Any Location') c++;
    if (selectedCompSize  !== 'Any Size')     c++;
    if (selectedDatePost  !== 'Any Time')     c++;
    if (remoteOnly) c++;
    if (urgentOnly) c++;
    setFilterCount(c);
  };

  const resetFilters = () => {
    setSelectedJobType('All Types'); setSelectedExp('All Levels');
    setSelectedSalary(0); setSelectedLocation('Any Location');
    setSelectedCompSize('Any Size'); setSelectedDatePost('Any Time');
    setRemoteOnly(false); setUrgentOnly(false);
    setSelectedSort('Best Match'); setSearchQuery(''); setSelectedCategory('all');
  };

  // ─── Actions ───────────────────────────────────────────────────────────────
  const openJob = async (job) => {
    setSelectedJob(job);
    setJobModal(true);
    // Track recently viewed
    const rv = [job, ...recentlyViewed.filter(j=>String(j.id)!==String(job.id))].slice(0,10);
    setRecentlyViewed(rv);
    await AsyncStorage.setItem('recently_viewed_jobs', JSON.stringify(rv));
  };

  const saveSearch = async (q) => {
    if (!q.trim()) return;
    const rs = [q, ...recentSearches.filter(r=>r!==q)].slice(0,8);
    setRecentSearches(rs);
    await AsyncStorage.setItem('recent_job_searches', JSON.stringify(rs));
  };

  const handleSearch = (q) => {
    setSearchQuery(q);
    if (q.length > 2) saveSearch(q);
  };

  const toggleSave = async (job) => {
    const jobId = String(job.id||job._id);
    const isSaved = savedIds.has(jobId);
    const newSet = new Set(savedIds);
    if (isSaved) {
      newSet.delete(jobId);
      setSavedJobsData(prev => prev.filter(j=>String(j.id||j.job_id)!==jobId));
      await apiFetch(`/api/professional/saved-jobs/${jobId}`, { method:'DELETE' });
    } else {
      newSet.add(jobId);
      const newJob = { ...job, savedAt: new Date().toISOString() };
      setSavedJobsData(prev => [...prev, newJob]);
      await apiFetch('/api/professional/saved-jobs', { method:'POST', body: JSON.stringify({ user_id: userId, job_id: jobId }) });
    }
    setSavedIds(newSet);
    await AsyncStorage.setItem(`saved_jobs_data_${userId||'g'}`, JSON.stringify(
      isSaved ? savedJobsData.filter(j=>String(j.id||j.job_id)!==jobId) : [...savedJobsData, { ...job, savedAt: new Date().toISOString() }]
    ));
  };

  const handleApply = (job) => {
    setJobModal(false);
    navigation.navigate('SubmitForJobs', { jobId: String(job.id||job._id), job });
  };

  const handleShare = async (job) => {
    try {
      await Share.share({
        title: job.title,
        message: `${job.title} at ${job.company||'a great company'}\n${formatSalary(job)} | ${job.location||'UK'}\n\nApply via JobEng app`,
      });
    } catch {}
  };

  const toggleCompare = (job) => {
    const id = String(job.id);
    const alreadyIn = compareJobs.find(j=>String(j.id)===id);
    if (alreadyIn) {
      setCompareJobs(prev => prev.filter(j=>String(j.id)!==id));
    } else {
      if (compareJobs.length >= 2) {
        Alert.alert('Compare limit', 'You can compare up to 2 jobs at a time.'); return;
      }
      setCompareJobs(prev => [...prev, job]);
    }
  };

  const generatePrepQuestions = async (job) => {
    setGeneratingPrep(true);
    setPrepModal(true);
    try {
      const r = await apiFetch('/api/recruitment/interview-questions', {
        method:'POST',
        body: JSON.stringify({ job_title: job.title, skills: job.skills||job.requiredSkills||[], level: job.experienceLevel||'' }),
      });
      if (!r.error && r.questions) { setPrepQuestions(r.questions); return; }
    } catch {}
    // Fallback built-in questions
    setPrepQuestions([
      `Tell me about your experience relevant to ${job.title}.`,
      `What drew you to apply for this ${job.title} role?`,
      `Describe a challenging project and how you overcame it.`,
      `How do you stay up to date with developments in ${(job.skills||[])[0]||'your field'}?`,
      `Where do you see yourself in 5 years in this industry?`,
      `What's your approach to working in a team on tight deadlines?`,
      `Give an example of when you improved a process or system.`,
      `What salary expectations do you have for this role?`,
      `Do you have any questions for us?`,
    ]);
    setGeneratingPrep(false);
  };

  const saveNote = async () => {
    if (!selectedJob) return;
    const jobId = String(selectedJob.id);
    const updated = { ...jobNotes, [jobId]: noteDraft };
    setJobNotes(updated);
    setNoteModal(false);
    await AsyncStorage.setItem(`job_notes_${userId||'g'}`, JSON.stringify(updated));
  };

  const saveAlert = async () => {
    if (!alertForm.title.trim() && !alertForm.keywords.trim()) {
      Alert.alert('Required', 'Please enter keywords or a title for your alert.'); return;
    }
    setSavingAlert(true);
    try {
      const body = { ...alertForm, user_id: userId, created_at: new Date().toISOString() };
      if (editAlert) {
        const r = await apiFetch(`/api/professional/alerts/${editAlert.id}`, { method:'PUT', body: JSON.stringify(body) });
        setAlerts(prev => prev.map(a => a.id===editAlert.id ? { ...a, ...body } : a));
      } else {
        const r = await apiFetch('/api/professional/alerts', { method:'POST', body: JSON.stringify(body) });
        const newAlert = { ...body, id: r.id||r.alert?.id||Date.now() };
        setAlerts(prev => [...prev, newAlert]);
        await AsyncStorage.setItem(`job_alerts_${userId||'g'}`, JSON.stringify([...alerts, newAlert]));
      }
      setAlertModal(false);
      setEditAlert(null);
      setAlertForm({ title:'', keywords:'', location:'', jobType:'All Types', minSalary:'', frequency:'Daily Digest', active:true });
    } catch { Alert.alert('Error', 'Could not save alert.'); }
    finally { setSavingAlert(false); }
  };

  const deleteAlert = (alert) => {
    Alert.alert('Delete Alert', `Delete "${alert.title||alert.keywords}"?`, [
      { text:'Cancel', style:'cancel' },
      { text:'Delete', style:'destructive', onPress: async () => {
        await apiFetch(`/api/professional/alerts/${alert.id}`, { method:'DELETE' });
        setAlerts(prev => prev.filter(a=>a.id!==alert.id));
      }},
    ]);
  };

  const toggleAlert = async (alert) => {
    const updated = { ...alert, active: !alert.active };
    await apiFetch(`/api/professional/alerts/${alert.id}`, { method:'PUT', body: JSON.stringify(updated) });
    setAlerts(prev => prev.map(a => a.id===alert.id ? updated : a));
  };

  const submitReport = () => {
    if (!reportReason.trim()) { Alert.alert('Required','Please describe the issue.'); return; }
    setReportModal(false);
    setReportReason('');
    Alert.alert('Report Submitted','Thank you. We will review this job posting.');
  };

  // ─── Render helpers ────────────────────────────────────────────────────────
  const renderJobCard = (job, isGrid = false) => {
    const jobId    = String(job.id||job._id);
    const isSaved  = savedIds.has(jobId);
    const isApplied= appliedIds.has(jobId);
    const ms       = getMatchScore(job, userSkills);
    const isNew    = (Date.now()-new Date(job.createdAt||job.created_at||0).getTime()) < 3*86400000;
    const dl       = daysLeft(job.deadline||job.closingDate||job.closing_date);
    const urgent   = dl !== null && dl <= 5;
    const inCompare= compareJobs.some(j=>String(j.id)===jobId);

    if (isGrid) return (
      <TouchableOpacity style={[s.gridCard, inCompare && s.gridCardSelected, urgent && s.gridCardUrgent]}
        onPress={() => compareMode ? toggleCompare(job) : openJob(job)} activeOpacity={0.85}
      >
        <View style={s.gridLogoBox}>
          <Text style={s.gridEmoji}>{job.logo||getEmoji(job.title)}</Text>
        </View>
        <Text style={s.gridTitle} numberOfLines={2}>{job.title}</Text>
        <Text style={s.gridCompany} numberOfLines={1}>{job.company||job.department||'Company'}</Text>
        <Text style={s.gridSalary}>{formatSalary(job)}</Text>
        <View style={s.gridFooter}>
          {ms !== null && <View style={[s.gridMatchBadge,{backgroundColor:matchColor(ms)+'22'}]}>
            <Text style={[s.gridMatchText,{color:matchColor(ms)}]}>{ms}%</Text>
          </View>}
          <TouchableOpacity onPress={()=>toggleSave(job)} style={s.gridSaveBtn}>
            <Ionicons name={isSaved?'bookmark':'bookmark-outline'} size={16} color={isSaved?'#0e4e96':'#94a3b8'} />
          </TouchableOpacity>
        </View>
        {isApplied && <View style={s.gridAppliedBadge}><Text style={s.gridAppliedText}>✓</Text></View>}
        {inCompare && <View style={s.gridCompareBadge}><Text style={s.gridCompareText}>Compare</Text></View>}
      </TouchableOpacity>
    );

    return (
      <Animated.View style={{ opacity: fadeAnim }}>
        <TouchableOpacity
          style={[s.jobCard, isApplied && s.jobCardApplied, urgent && s.jobCardUrgent, inCompare && s.jobCardCompare]}
          onPress={() => compareMode ? toggleCompare(job) : openJob(job)}
          activeOpacity={0.88}
        >
          {/* Top badges row */}
          <View style={s.badgeRow}>
            {isNew    && <View style={s.newBadge}><Text style={s.newBadgeText}>NEW</Text></View>}
            {urgent   && <View style={s.urgentBadge}><MaterialCommunityIcons name="fire" size={10} color="#FFF"/><Text style={s.urgentBadgeText}>{dl}d left</Text></View>}
            {isApplied&& <View style={s.appliedBadge}><Text style={s.appliedBadgeText}>✓ Applied</Text></View>}
            {ms !== null && (
              <View style={[s.matchBadge, { backgroundColor: matchColor(ms) }]}>
                <Text style={s.matchBadgeText}>{ms}% match</Text>
              </View>
            )}
            {inCompare && <View style={s.compareBadge}><Text style={s.compareBadgeText}>Compare</Text></View>}
            <TouchableOpacity style={s.saveBtn} onPress={()=>toggleSave(job)}>
              <Ionicons name={isSaved?'bookmark':'bookmark-outline'} size={20} color={isSaved?'#0e4e96':'#94a3b8'} />
            </TouchableOpacity>
          </View>

          {/* Header */}
          <View style={s.cardHeader}>
            <View style={s.logoBox}>
              <Text style={s.logoEmoji}>{job.logo||getEmoji(job.title)}</Text>
            </View>
            <View style={{ flex:1 }}>
              <Text style={s.cardTitle} numberOfLines={2}>{job.title}</Text>
              <Text style={s.cardCompany} numberOfLines={1}>{job.company||job.department||'Company'}</Text>
            </View>
          </View>

          {/* Meta grid */}
          <View style={s.metaGrid}>
            {[
              { icon:'map-marker-outline',  val: job.location||'UK'                              },
              { icon:'currency-gbp',         val: formatSalary(job)                              },
              { icon:'briefcase-outline',    val: job.type||job.jobType||job.job_type||'Full-time'},
              { icon:'clock-outline',        val: job.posted||formatPosted(job.createdAt||job.created_at) },
            ].map((m,i) => (
              <View key={i} style={s.metaRow}>
                <MaterialCommunityIcons name={m.icon} size={13} color="#94a3b8" />
                <Text style={s.metaText} numberOfLines={1}>{m.val}</Text>
              </View>
            ))}
          </View>

          {/* Skills */}
          {toSkillStrings(job).length > 0 && (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.skillsRow}>
              {toSkillStrings(job).slice(0,6).map((sk,i) => {
                const isMatch = userSkills.some(us => sk.toLowerCase().includes(String(us).toLowerCase()));
                return (
                  <View key={i} style={[s.skillChip, isMatch && s.skillChipMatch]}>
                    <Text style={[s.skillChipText, isMatch && s.skillChipTextMatch]}>{sk}</Text>
                  </View>
                );
              })}
            </ScrollView>
          )}

          {/* Deadline bar */}
          {dl !== null && dl <= 14 && (
            <View style={[s.deadlineStrip, { backgroundColor: dl<=3?'#FEF2F2':dl<=7?'#FFFBEB':'#F0FDF4' }]}>
              <MaterialCommunityIcons name="calendar-clock" size={13} color={dl<=3?'#EF4444':dl<=7?'#F59E0B':'#10B981'} />
              <Text style={[s.deadlineText, { color: dl<=3?'#EF4444':dl<=7?'#F59E0B':'#10B981' }]}>
                {dl===0 ? 'Closing today' : `Closes in ${dl} day${dl!==1?'s':''}`}
              </Text>
            </View>
          )}

          {/* Note indicator */}
          {jobNotes[String(job.id)] && (
            <View style={s.noteStrip}>
              <MaterialCommunityIcons name="note-text" size={12} color="#6B7280" />
              <Text style={s.noteStripText} numberOfLines={1}>{jobNotes[String(job.id)]}</Text>
            </View>
          )}

          {/* Actions */}
          <View style={s.cardActions}>
            <TouchableOpacity style={s.detailsBtn} onPress={()=>openJob(job)}>
              <Text style={s.detailsBtnText}>View Details</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[s.applyBtn, isApplied && s.applyBtnApplied]}
              onPress={() => !isApplied && handleApply(job)}
            >
              <Text style={s.applyBtnText}>{isApplied ? '✓ Applied' : 'Apply Now'}</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Animated.View>
    );
  };

  // ─── Tab: Browse ──────────────────────────────────────────────────────────
  const renderBrowse = () => (
    <View style={{ flex:1 }}>
      {/* Recent searches */}
      {!searchQuery && recentSearches.length > 0 && (
        <View style={s.recentRow}>
          <Text style={s.recentLabel}>Recent:</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {recentSearches.map((r,i) => (
              <TouchableOpacity key={i} style={s.recentChip} onPress={() => setSearchQuery(r)}>
                <Ionicons name="time-outline" size={12} color="#94a3b8" />
                <Text style={s.recentChipText}>{r}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}

      {/* Recently viewed */}
      {!searchQuery && recentlyViewed.length > 0 && (
        <View style={s.rvSection}>
          <Text style={s.rvTitle}>Recently Viewed</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {recentlyViewed.slice(0,5).map(j => (
              <TouchableOpacity key={j.id} style={s.rvCard} onPress={()=>openJob(j)}>
                <Text style={s.rvEmoji}>{j.logo||getEmoji(j.title)}</Text>
                <Text style={s.rvJobTitle} numberOfLines={2}>{j.title}</Text>
                <Text style={s.rvSalary}>{formatSalary(j)}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}

      {/* Stats bar */}
      <View style={s.statsBar}>
        <Text style={s.statsText}>
          {visibleJobs.length} of {jobs.length} jobs
          {selectedCategory!=='all' ? ` in ${selectedCategory}` : ''}
        </Text>
        <View style={s.statsRight}>
          {compareMode && compareJobs.length > 0 && (
            <TouchableOpacity style={s.compareBtn} onPress={() => compareJobs.length===2 ? setCompareModal(true) : Alert.alert('Select 2','Please select 2 jobs to compare.')}>
              <MaterialCommunityIcons name="compare" size={14} color="#FFF" />
              <Text style={s.compareBtnText}>Compare ({compareJobs.length})</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity onPress={() => setViewMode(v => v==='list'?'grid':'list')} style={s.viewModeBtn}>
            <MaterialCommunityIcons name={viewMode==='list'?'view-grid':'format-list-bulleted'} size={18} color="#0e4e96" />
          </TouchableOpacity>
        </View>
      </View>

      {/* Active filter chips */}
      {filterCount > 0 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.activeFiltersBar} contentContainerStyle={{ paddingHorizontal:16, gap:8 }}>
          {selectedJobType!=='All Types' && <FilterChip label={selectedJobType} onRemove={()=>setSelectedJobType('All Types')} />}
          {selectedExp!=='All Levels'    && <FilterChip label={selectedExp}     onRemove={()=>setSelectedExp('All Levels')}    />}
          {selectedSalary!==0            && <FilterChip label={SALARY_RANGES[selectedSalary].label} onRemove={()=>setSelectedSalary(0)} />}
          {selectedLocation!=='Any Location' && <FilterChip label={selectedLocation} onRemove={()=>setSelectedLocation('Any Location')} />}
          {selectedDatePost!=='Any Time' && <FilterChip label={selectedDatePost} onRemove={()=>setSelectedDatePost('Any Time')} />}
          {remoteOnly && <FilterChip label="Remote Only" onRemove={()=>setRemoteOnly(false)} />}
          {urgentOnly && <FilterChip label="Urgent Only" onRemove={()=>setUrgentOnly(false)} />}
          <TouchableOpacity style={s.clearAllBtn} onPress={resetFilters}>
            <Text style={s.clearAllText}>Clear all</Text>
          </TouchableOpacity>
        </ScrollView>
      )}

      {loading ? (
        <View style={s.loadingBox}>
          <ActivityIndicator size={44} color="#0e4e96" />
          <Text style={s.loadingText}>Finding jobs for you…</Text>
        </View>
      ) : visibleJobs.length === 0 ? (
        <View style={s.emptyBox}>
          <MaterialCommunityIcons name="file-search-outline" size={64} color="#94a3b8" />
          <Text style={s.emptyTitle}>No jobs found</Text>
          <Text style={s.emptySubtitle}>Try adjusting your search or filters</Text>
          <TouchableOpacity style={s.emptyResetBtn} onPress={resetFilters}>
            <Text style={s.emptyResetText}>Reset Filters</Text>
          </TouchableOpacity>
        </View>
      ) : viewMode === 'grid' ? (
        <FlatList
          data={visibleJobs}
          numColumns={2}
          keyExtractor={item => String(item.id||item._id||Math.random())}
          contentContainerStyle={{ padding:16, paddingBottom:100 }}
          columnWrapperStyle={{ gap:12, marginBottom:12 }}
          renderItem={({ item }) => renderJobCard(item, true)}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={()=>{setRefreshing(true);loadJobs(true);}} colors={['#0e4e96']} />}
        />
      ) : (
        <FlatList
          data={visibleJobs}
          keyExtractor={item => String(item.id||item._id||Math.random())}
          contentContainerStyle={{ padding:16, paddingBottom:100 }}
          renderItem={({ item }) => renderJobCard(item, false)}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={()=>{setRefreshing(true);loadJobs(true);}} colors={['#0e4e96']} />}
          initialNumToRender={6}
          maxToRenderPerBatch={8}
          windowSize={12}
        />
      )}
    </View>
  );

  // ─── Tab: Saved ───────────────────────────────────────────────────────────
  const renderSaved = () => (
    <View style={{ flex:1 }}>
      <View style={s.tabHeaderBar}>
        <Text style={s.tabHeaderTitle}>{savedJobsData.length} Saved Jobs</Text>
        {savedJobsData.length > 0 && (
          <TouchableOpacity onPress={()=>Alert.alert('Clear All','Remove all saved jobs?',[{text:'Cancel',style:'cancel'},{text:'Clear',style:'destructive',onPress:()=>{setSavedJobsData([]);setSavedIds(new Set());AsyncStorage.removeItem(`saved_jobs_data_${userId||'g'}`);}}])}>
            <Text style={s.tabHeaderAction}>Clear all</Text>
          </TouchableOpacity>
        )}
      </View>
      {savedJobsData.length === 0 ? (
        <View style={s.emptyBox}>
          <MaterialCommunityIcons name="bookmark-outline" size={64} color="#94a3b8" />
          <Text style={s.emptyTitle}>No saved jobs</Text>
          <Text style={s.emptySubtitle}>Bookmark jobs you're interested in to view them here</Text>
        </View>
      ) : (
        <FlatList
          data={savedJobsData}
          keyExtractor={item => String(item.id||item._id||Math.random())}
          contentContainerStyle={{ padding:16, paddingBottom:100 }}
          renderItem={({ item }) => renderJobCard(item, false)}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={()=>{setRefreshing(true);loadSavedJobs(userId).finally(()=>setRefreshing(false));}} colors={['#0e4e96']} />}
        />
      )}
    </View>
  );

  // ─── Tab: Applied ─────────────────────────────────────────────────────────
  const renderApplied = () => (
    <View style={{ flex:1 }}>
      <View style={s.tabHeaderBar}>
        <Text style={s.tabHeaderTitle}>{appliedJobs.length} Applications</Text>
      </View>
      {appliedJobs.length === 0 ? (
        <View style={s.emptyBox}>
          <MaterialCommunityIcons name="send-outline" size={64} color="#94a3b8" />
          <Text style={s.emptyTitle}>No applications yet</Text>
          <Text style={s.emptySubtitle}>Jobs you apply for will appear here with status tracking</Text>
        </View>
      ) : (
        <FlatList
          data={appliedJobs}
          keyExtractor={item=>String(item.id||item.application_id||Math.random())}
          contentContainerStyle={{ padding:16, paddingBottom:100 }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={()=>{setRefreshing(true);loadAppliedJobs(userId).finally(()=>setRefreshing(false));}} colors={['#0e4e96']} />}
          renderItem={({ item }) => {
            const status = item.status||'submitted';
            const st     = APP_STATUSES[status] || APP_STATUSES.submitted;
            const stageIdx = Object.keys(APP_STATUSES).indexOf(status);
            const totalStages = Object.keys(APP_STATUSES).length - 2; // exclude rejected/withdrawn
            return (
              <TouchableOpacity style={s.appCard} onPress={() => {
                const jobData = jobs.find(j=>String(j.id)===String(item.job_id||item.id));
                if (jobData) openJob(jobData);
              }}>
                <View style={s.appCardHeader}>
                  <View style={s.logoBox}>
                    <Text style={s.logoEmoji}>{getEmoji(item.job_title||item.title||'')}</Text>
                  </View>
                  <View style={{ flex:1 }}>
                    <Text style={s.appJobTitle} numberOfLines={1}>{item.job_title||item.title||'Job Application'}</Text>
                    <Text style={s.appCompany}>{item.company||item.department||'Company'}</Text>
                    <Text style={s.appDate}>Applied {formatPosted(item.applied_at||item.created_at)}</Text>
                  </View>
                  <View style={[s.statusBadge, { backgroundColor: st.color+'22' }]}>
                    <MaterialCommunityIcons name={st.icon} size={14} color={st.color} />
                    <Text style={[s.statusBadgeText, { color: st.color }]}>{st.label}</Text>
                  </View>
                </View>

                {/* Progress tracker */}
                {status !== 'rejected' && status !== 'withdrawn' && (
                  <View style={s.progressTracker}>
                    {Object.entries(APP_STATUSES).slice(0, 6).map(([k, v], i) => (
                      <View key={k} style={s.progressStep}>
                        <View style={[s.progressDot, { backgroundColor: i<=stageIdx ? v.color : '#E5E7EB' }]}>
                          {i < stageIdx && <MaterialCommunityIcons name="check" size={10} color="#FFF" />}
                          {i === stageIdx && <View style={s.progressDotInner} />}
                        </View>
                        {i < 5 && <View style={[s.progressLine, { backgroundColor: i < stageIdx ? v.color : '#E5E7EB' }]} />}
                        <Text style={[s.progressLabel, i===stageIdx && { color:'#0e4e96', fontWeight:'700' }]} numberOfLines={1}>{v.label.split(' ')[0]}</Text>
                      </View>
                    ))}
                  </View>
                )}

                {item.feedback && (
                  <View style={s.feedbackBox}>
                    <MaterialCommunityIcons name="comment-text-outline" size={14} color="#6B7280" />
                    <Text style={s.feedbackText} numberOfLines={2}>{item.feedback}</Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          }}
        />
      )}
    </View>
  );

  // ─── Tab: Alerts ──────────────────────────────────────────────────────────
  const renderAlerts = () => (
    <View style={{ flex:1 }}>
      <View style={s.tabHeaderBar}>
        <Text style={s.tabHeaderTitle}>{alerts.length} Job Alerts</Text>
        <TouchableOpacity style={s.addAlertBtn} onPress={()=>{setEditAlert(null);setAlertForm({title:'',keywords:'',location:'',jobType:'All Types',minSalary:'',frequency:'Daily Digest',active:true});setAlertModal(true);}}>
          <Ionicons name="add" size={18} color="#FFF" />
          <Text style={s.addAlertText}>New Alert</Text>
        </TouchableOpacity>
      </View>

      {alerts.length === 0 ? (
        <View style={s.emptyBox}>
          <MaterialCommunityIcons name="bell-outline" size={64} color="#94a3b8" />
          <Text style={s.emptyTitle}>No job alerts</Text>
          <Text style={s.emptySubtitle}>Create alerts to get notified when matching jobs are posted</Text>
          <TouchableOpacity style={[s.emptyResetBtn,{marginTop:20}]} onPress={()=>setAlertModal(true)}>
            <Text style={s.emptyResetText}>Create First Alert</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <FlatList
          data={alerts}
          keyExtractor={item=>String(item.id||Math.random())}
          contentContainerStyle={{ padding:16, paddingBottom:100 }}
          renderItem={({ item }) => (
            <View style={s.alertCard}>
              <View style={s.alertCardHeader}>
                <View style={[s.alertIcon, { backgroundColor: item.active?'#EEF2FF':'#F3F4F6' }]}>
                  <MaterialCommunityIcons name="bell" size={20} color={item.active?'#0e4e96':'#9CA3AF'} />
                </View>
                <View style={{ flex:1, marginLeft:12 }}>
                  <Text style={s.alertTitle}>{item.title||item.keywords||'Job Alert'}</Text>
                  <Text style={s.alertMeta}>
                    {[item.keywords,item.location,item.jobType!=='All Types'&&item.jobType].filter(Boolean).join(' · ')}
                  </Text>
                  <Text style={s.alertFreq}>{item.frequency||'Daily Digest'}</Text>
                </View>
                <Switch value={item.active||false} onValueChange={()=>toggleAlert(item)}
                  trackColor={{false:'#D1D5DB',true:'#BFDBFE'}} thumbColor={item.active?'#0e4e96':'#9CA3AF'} />
              </View>
              <View style={s.alertCardFooter}>
                <Text style={s.alertFooterText}>Last triggered: {fmtDate(item.last_triggered)}</Text>
                <TouchableOpacity style={s.alertEditBtn} onPress={()=>{setEditAlert(item);setAlertForm({title:item.title||'',keywords:item.keywords||'',location:item.location||'',jobType:item.jobType||'All Types',minSalary:String(item.minSalary||''),frequency:item.frequency||'Daily Digest',active:item.active||true});setAlertModal(true);}}>
                  <MaterialCommunityIcons name="pencil" size={14} color="#3B82F6" />
                  <Text style={[s.alertEditText,{color:'#3B82F6'}]}>Edit</Text>
                </TouchableOpacity>
                <TouchableOpacity style={s.alertEditBtn} onPress={()=>deleteAlert(item)}>
                  <MaterialCommunityIcons name="delete" size={14} color="#EF4444" />
                  <Text style={[s.alertEditText,{color:'#EF4444'}]}>Delete</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        />
      )}
    </View>
  );

  // ─── Job Detail Modal ──────────────────────────────────────────────────────
  const renderJobModal = () => {
    const job = selectedJob;
    if (!job) return null;
    const jobId    = String(job.id||job._id);
    const isSaved  = savedIds.has(jobId);
    const isApplied= appliedIds.has(jobId);
    const ms       = getMatchScore(job, userSkills);
    const skills   = toSkillStrings(job);
    const reqs     = job.requirements||job.qualifications||[];
    const benefits = job.benefits||[];
    const dl       = daysLeft(job.deadline||job.closingDate||job.closing_date);
    const myNote   = jobNotes[jobId] || '';

    // Similar jobs
    const similar = jobs.filter(j => {
      if (String(j.id)===jobId) return false;
      return j.title?.toLowerCase().includes((job.title||'').split(' ')[0]?.toLowerCase()) ||
             (j.department||'')===job.department;
    }).slice(0,3);

    return (
      <Modal animationType="slide" transparent visible={jobModal} onRequestClose={()=>setJobModal(false)}>
        <View style={s.modalOverlay}>
          <View style={[s.modalSheet, { maxHeight: height*0.94 }]}>
            {/* Header */}
            <View style={s.modalHeader}>
              <TouchableOpacity onPress={()=>setJobModal(false)} style={s.modalBackBtn}>
                <Ionicons name="arrow-back" size={22} color="#1e293b" />
              </TouchableOpacity>
              <Text style={s.modalHeaderTitle} numberOfLines={1}>{job.title}</Text>
              <View style={{ flexDirection:'row', gap:12 }}>
                <TouchableOpacity onPress={()=>handleShare(job)}>
                  <Ionicons name="share-social-outline" size={22} color="#64748b" />
                </TouchableOpacity>
                <TouchableOpacity onPress={()=>toggleSave(job)}>
                  <Ionicons name={isSaved?'bookmark':'bookmark-outline'} size={22} color={isSaved?'#0e4e96':'#64748b'} />
                </TouchableOpacity>
              </View>
            </View>

            <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom:120 }}>
              {/* Company hero */}
              <View style={s.jobHero}>
                <View style={s.heroLogoBox}>
                  <Text style={{ fontSize:42 }}>{job.logo||getEmoji(job.title)}</Text>
                </View>
                <Text style={s.heroJobTitle}>{job.title}</Text>
                <Text style={s.heroCompany}>{job.company||job.department||'Company'}</Text>
                <View style={s.heroLocationRow}>
                  <Ionicons name="location-outline" size={15} color="#64748b" />
                  <Text style={s.heroLocation}>{job.location||'UK'}</Text>
                </View>
                {/* Social proof */}
                {(job.applicants||job.views) && (
                  <View style={s.socialProof}>
                    {job.applicants && <Text style={s.socialText}>{job.applicants} applicants</Text>}
                    {job.views && <Text style={s.socialText}>{job.views} views</Text>}
                  </View>
                )}
              </View>

              {/* Match score */}
              {ms !== null && (
                <View style={[s.matchBox, { borderColor: matchColor(ms)+'44' }]}>
                  <View style={{ flexDirection:'row', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                    <Text style={s.matchBoxTitle}>Profile Match</Text>
                    <Text style={[s.matchBoxScore, { color: matchColor(ms) }]}>{ms}%</Text>
                  </View>
                  <View style={s.matchBar}>
                    <View style={[s.matchBarFill, { width:`${ms}%`, backgroundColor: matchColor(ms) }]} />
                  </View>
                  <Text style={s.matchBoxSub}>
                    {ms >= 75 ? '🎯 Strong match — you have most required skills' :
                     ms >= 50 ? '👍 Good match — you meet key requirements' :
                     '📚 Partial match — consider upskilling in missing areas'}
                  </Text>
                </View>
              )}

              {/* Key stats row */}
              <View style={s.statsRow}>
                {[
                  { icon:'currency-gbp',      label:'Salary',   val: formatSalary(job)                           },
                  { icon:'briefcase-outline',  label:'Type',     val: job.type||job.jobType||'Full-time'          },
                  { icon:'account-school',     label:'Level',    val: job.experienceLevel||job.experience_level||'Any' },
                  { icon:'clock-outline',      label:'Posted',   val: formatPosted(job.createdAt||job.created_at) },
                ].map((k,i) => (
                  <View key={i} style={s.statItem}>
                    <MaterialCommunityIcons name={k.icon} size={20} color="#0e4e96" />
                    <Text style={s.statLabel}>{k.label}</Text>
                    <Text style={s.statVal} numberOfLines={1}>{k.val}</Text>
                  </View>
                ))}
              </View>

              {/* Deadline */}
              {dl !== null && (
                <View style={[s.deadlineBanner, { backgroundColor: dl<=3?'#FEF2F2':dl<=7?'#FFFBEB':'#F0FDF4', borderColor: dl<=3?'#FCA5A5':dl<=7?'#FDE68A':'#86EFAC' }]}>
                  <MaterialCommunityIcons name="calendar-alert" size={18} color={dl<=3?'#EF4444':dl<=7?'#F59E0B':'#10B981'} />
                  <Text style={[s.deadlineBannerText, { color: dl<=3?'#EF4444':dl<=7?'#F59E0B':'#10B981' }]}>
                    {dl===0 ? 'Closing today! Apply now' : `Application closes in ${dl} day${dl!==1?'s':''}`}
                  </Text>
                </View>
              )}

              {/* Quick actions */}
              <View style={s.quickActionsRow}>
                {[
                  { icon:'note-plus-outline',   label:'Add Note',        action:()=>{setNoteDraft(myNote);setNoteModal(true);}                              },
                  { icon:'comment-question',     label:'Interview Prep',  action:()=>generatePrepQuestions(job)                                            },
                  { icon:'compare',              label:'Compare',         action:()=>{toggleCompare(job);setCompareMode(true);}                             },
                  { icon:'chart-line',           label:'Salary Info',     action:()=>setSalaryModal(true)                                                  },
                  { icon:'flag-outline',         label:'Report Job',      action:()=>setReportModal(true)                                                  },
                ].map(qa => (
                  <TouchableOpacity key={qa.label} style={s.quickActionBtn} onPress={qa.action}>
                    <View style={s.quickActionIcon}>
                      <MaterialCommunityIcons name={qa.icon} size={18} color="#0e4e96" />
                    </View>
                    <Text style={s.quickActionLabel}>{qa.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* My Note */}
              {myNote ? (
                <TouchableOpacity style={s.myNoteBox} onPress={()=>{setNoteDraft(myNote);setNoteModal(true);}}>
                  <MaterialCommunityIcons name="note-text" size={16} color="#F59E0B" />
                  <Text style={s.myNoteText} numberOfLines={2}>{myNote}</Text>
                  <MaterialCommunityIcons name="pencil" size={14} color="#9CA3AF" />
                </TouchableOpacity>
              ) : null}

              {/* Description */}
              <View style={s.section}>
                <Text style={s.sectionTitle}>Job Description</Text>
                <Text style={s.sectionBody}>
                  {job.description || `We are looking for a talented ${job.title} to join our growing team. This is an exciting opportunity to work on cutting-edge projects in a collaborative and innovative environment.\n\nThe ideal candidate will bring strong technical expertise, excellent communication skills, and a passion for engineering excellence.`}
                </Text>
              </View>

              {/* Required Skills */}
              {skills.length > 0 && (
                <View style={s.section}>
                  <Text style={s.sectionTitle}>Required Skills</Text>
                  <View style={s.skillsWrap}>
                    {skills.map((sk,i) => {
                      const isMatch = userSkills.some(us => sk.toLowerCase().includes(String(us).toLowerCase()));
                      return (
                        <View key={i} style={[s.detailSkill, isMatch && s.detailSkillMatch]}>
                          {isMatch && <Ionicons name="checkmark-circle" size={13} color="#10B981" style={{ marginRight:4 }} />}
                          <Text style={[s.detailSkillText, isMatch && s.detailSkillTextMatch]}>{sk}</Text>
                        </View>
                      );
                    })}
                  </View>
                  {userSkills.length > 0 && (
                    <Text style={s.skillsGap}>
                      {skills.filter(sk => !userSkills.some(us=>sk.toLowerCase().includes(us.toLowerCase()))).length > 0
                        ? `⚠️ Missing: ${skills.filter(sk => !userSkills.some(us=>sk.toLowerCase().includes(us.toLowerCase()))).slice(0,3).join(', ')}`
                        : '✅ You have all required skills!'
                      }
                    </Text>
                  )}
                </View>
              )}

              {/* Requirements */}
              {reqs.length > 0 && (
                <View style={s.section}>
                  <Text style={s.sectionTitle}>Requirements</Text>
                  {reqs.map((r,i) => (
                    <View key={i} style={s.bulletRow}>
                      <View style={s.bullet} />
                      <Text style={s.bulletText}>{r}</Text>
                    </View>
                  ))}
                </View>
              )}

              {/* Benefits */}
              {benefits.length > 0 && (
                <View style={s.section}>
                  <Text style={s.sectionTitle}>Benefits & Perks</Text>
                  <View style={s.benefitsGrid}>
                    {benefits.map((b,i) => (
                      <View key={i} style={s.benefitItem}>
                        <MaterialCommunityIcons name="check-circle" size={16} color="#10B981" />
                        <Text style={s.benefitText}>{b}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* Company info */}
              <View style={s.section}>
                <Text style={s.sectionTitle}>About the Company</Text>
                <View style={s.companyInfoCard}>
                  <Text style={s.companyInfoEmoji}>{getEmoji(job.title)}</Text>
                  <View style={{ flex:1, marginLeft:12 }}>
                    <Text style={s.companyInfoName}>{job.company||job.department||'Company'}</Text>
                    {job.companySize && <Text style={s.companyInfoMeta}>{job.companySize} employees</Text>}
                    {job.industry && <Text style={s.companyInfoMeta}>{job.industry}</Text>}
                  </View>
                </View>
                {job.companyDescription && (
                  <Text style={[s.sectionBody, { marginTop:8 }]}>{job.companyDescription}</Text>
                )}
              </View>

              {/* Similar jobs */}
              {similar.length > 0 && (
                <View style={s.section}>
                  <Text style={s.sectionTitle}>Similar Jobs</Text>
                  {similar.map(sj => (
                    <TouchableOpacity key={sj.id} style={s.similarJobRow} onPress={()=>{ setJobModal(false); setTimeout(()=>openJob(sj),200); }}>
                      <View style={s.similarLogo}>
                        <Text>{getEmoji(sj.title)}</Text>
                      </View>
                      <View style={{ flex:1, marginLeft:10 }}>
                        <Text style={s.similarTitle} numberOfLines={1}>{sj.title}</Text>
                        <Text style={s.similarMeta}>{sj.company||sj.department||'Company'} · {formatSalary(sj)}</Text>
                      </View>
                      <Ionicons name="chevron-forward" size={16} color="#94a3b8" />
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </ScrollView>

            {/* Sticky CTA */}
            <View style={s.modalCTA}>
              <TouchableOpacity style={s.modalSaveBtn} onPress={()=>toggleSave(job)}>
                <Ionicons name={isSaved?'bookmark':'bookmark-outline'} size={20} color="#0e4e96" />
              </TouchableOpacity>
              <TouchableOpacity style={s.modalShareBtn} onPress={()=>handleShare(job)}>
                <Ionicons name="share-social-outline" size={20} color="#0e4e96" />
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.modalApplyBtn, isApplied && s.modalApplyBtnApplied]}
                onPress={() => !isApplied && handleApply(job)}
                disabled={applying}
              >
                {applying
                  ? <ActivityIndicator size="small" color="#FFF" />
                  : <Text style={s.modalApplyText}>{isApplied ? '✓ Already Applied' : 'Apply Now'}</Text>
                }
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  // ─── Filter Modal ──────────────────────────────────────────────────────────
  const renderFilterModal = () => (
    <Modal visible={filterModal} animationType="slide" transparent onRequestClose={()=>setFilterModal(false)}>
      <View style={s.filterOverlay}>
        <View style={s.filterSheet}>
          <View style={s.filterHeader}>
            <Text style={s.filterTitle}>Filter & Sort</Text>
            <TouchableOpacity onPress={()=>setFilterModal(false)}>
              <Ionicons name="close" size={24} color="#1e293b" />
            </TouchableOpacity>
          </View>

          <ScrollView showsVerticalScrollIndicator={false}>
            {[
              { title:'Sort By',          opts: SORT_OPTIONS,      state: selectedSort,      set: setSelectedSort      },
              { title:'Job Type',          opts: JOB_TYPES,         state: selectedJobType,  set: setSelectedJobType   },
              { title:'Experience Level',  opts: EXPERIENCE_LEVELS, state: selectedExp,      set: setSelectedExp       },
              { title:'Date Posted',       opts: DATE_POSTED,       state: selectedDatePost, set: setSelectedDatePost  },
              { title:'Location',          opts: UK_LOCATIONS,      state: selectedLocation, set: setSelectedLocation  },
            ].map(section => (
              <View key={section.title}>
                <Text style={s.filterSection}>{section.title}</Text>
                <View style={s.filterGrid}>
                  {section.opts.map(opt => (
                    <TouchableOpacity key={opt} style={[s.filterChip, section.state===opt && s.filterChipActive]} onPress={()=>section.set(opt)}>
                      <Text style={[s.filterChipText, section.state===opt && s.filterChipTextActive]}>{opt}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            ))}

            {/* Salary range */}
            <Text style={s.filterSection}>Salary Range</Text>
            <View style={s.filterGrid}>
              {SALARY_RANGES.map((r,i) => (
                <TouchableOpacity key={i} style={[s.filterChip, selectedSalary===i && s.filterChipActive]} onPress={()=>setSelectedSalary(i)}>
                  <Text style={[s.filterChipText, selectedSalary===i && s.filterChipTextActive]}>{r.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Toggles */}
            {[
              { label:'Remote Only', sub:'Show only remote positions', state:remoteOnly, set:setRemoteOnly, icon:'home-outline' },
              { label:'Urgent Only', sub:'Closing within 7 days',      state:urgentOnly, set:setUrgentOnly, icon:'fire' },
            ].map(t => (
              <View key={t.label} style={s.filterToggleRow}>
                <View style={{ flexDirection:'row', alignItems:'center', gap:10, flex:1 }}>
                  <MaterialCommunityIcons name={t.icon} size={20} color="#0e4e96" />
                  <View>
                    <Text style={s.filterToggleLabel}>{t.label}</Text>
                    <Text style={s.filterToggleSub}>{t.sub}</Text>
                  </View>
                </View>
                <Switch value={t.state} onValueChange={t.set} trackColor={{false:'#D1D5DB',true:'#BFDBFE'}} thumbColor={t.state?'#0e4e96':'#9CA3AF'} />
              </View>
            ))}
          </ScrollView>

          <View style={s.filterFooter}>
            <TouchableOpacity style={s.filterResetBtn} onPress={resetFilters}>
              <Text style={s.filterResetText}>Reset All</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.filterApplyBtn} onPress={()=>setFilterModal(false)}>
              <Text style={s.filterApplyText}>Show {visibleJobs.length} Jobs</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // ─── Alert Modal ───────────────────────────────────────────────────────────
  const renderAlertModal = () => (
    <Modal visible={alertModal} animationType="slide" transparent onRequestClose={()=>setAlertModal(false)}>
      <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'} style={s.filterOverlay}>
        <View style={s.filterSheet}>
          <View style={s.filterHeader}>
            <Text style={s.filterTitle}>{editAlert ? 'Edit Alert' : '+ New Job Alert'}</Text>
            <TouchableOpacity onPress={()=>setAlertModal(false)}>
              <Ionicons name="close" size={24} color="#1e293b" />
            </TouchableOpacity>
          </View>
          <ScrollView>
            <Text style={s.fieldLabel}>Alert Name</Text>
            <TextInput style={s.field} placeholder="e.g. Senior Software Engineer" placeholderTextColor="#BBB"
              value={alertForm.title} onChangeText={v=>setAlertForm(f=>({...f,title:v}))} />
            <Text style={s.fieldLabel}>Keywords *</Text>
            <TextInput style={s.field} placeholder="e.g. Python, React, DevOps" placeholderTextColor="#BBB"
              value={alertForm.keywords} onChangeText={v=>setAlertForm(f=>({...f,keywords:v}))} />
            <Text style={s.fieldLabel}>Location</Text>
            <TextInput style={s.field} placeholder="e.g. London, Remote" placeholderTextColor="#BBB"
              value={alertForm.location} onChangeText={v=>setAlertForm(f=>({...f,location:v}))} />
            <Text style={s.fieldLabel}>Minimum Salary (£)</Text>
            <TextInput style={s.field} placeholder="e.g. 50000" placeholderTextColor="#BBB"
              value={alertForm.minSalary} keyboardType="numeric" onChangeText={v=>setAlertForm(f=>({...f,minSalary:v}))} />
            <Text style={s.fieldLabel}>Job Type</Text>
            <View style={s.filterGrid}>
              {JOB_TYPES.map(t => (
                <TouchableOpacity key={t} style={[s.filterChip, alertForm.jobType===t && s.filterChipActive]} onPress={()=>setAlertForm(f=>({...f,jobType:t}))}>
                  <Text style={[s.filterChipText, alertForm.jobType===t && s.filterChipTextActive]}>{t}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={[s.fieldLabel, { marginTop:14 }]}>Alert Frequency</Text>
            <View style={s.filterGrid}>
              {ALERT_FREQUENCIES.map(f => (
                <TouchableOpacity key={f} style={[s.filterChip, alertForm.frequency===f && s.filterChipActive]} onPress={()=>setAlertForm(af=>({...af,frequency:f}))}>
                  <Text style={[s.filterChipText, alertForm.frequency===f && s.filterChipTextActive]}>{f}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <View style={[s.filterToggleRow, { marginTop:16 }]}>
              <Text style={s.filterToggleLabel}>Alert Active</Text>
              <Switch value={alertForm.active} onValueChange={v=>setAlertForm(f=>({...f,active:v}))} trackColor={{false:'#D1D5DB',true:'#BFDBFE'}} thumbColor={alertForm.active?'#0e4e96':'#9CA3AF'} />
            </View>
          </ScrollView>
          <View style={s.filterFooter}>
            <TouchableOpacity style={s.filterResetBtn} onPress={()=>setAlertModal(false)}>
              <Text style={s.filterResetText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.filterApplyBtn} onPress={saveAlert} disabled={savingAlert}>
              {savingAlert ? <ActivityIndicator size={16} color="#FFF" /> : <Text style={s.filterApplyText}>{editAlert?'Update Alert':'Create Alert'}</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );

  // ─── Compare Modal ─────────────────────────────────────────────────────────
  const renderCompareModal = () => {
    if (compareJobs.length < 2) return null;
    const [a, b] = compareJobs;
    const msA = getMatchScore(a, userSkills);
    const msB = getMatchScore(b, userSkills);
    const rows = [
      { label:'Salary',      a: formatSalary(a),                   b: formatSalary(b)                   },
      { label:'Type',        a: a.type||a.jobType||'Full-time',    b: b.type||b.jobType||'Full-time'    },
      { label:'Location',    a: a.location||'UK',                  b: b.location||'UK'                  },
      { label:'Experience',  a: a.experienceLevel||'Any',          b: b.experienceLevel||'Any'          },
      { label:'Posted',      a: formatPosted(a.createdAt||a.created_at), b: formatPosted(b.createdAt||b.created_at) },
      { label:'Match Score', a: msA!==null?`${msA}%`:'—',         b: msB!==null?`${msB}%`:'—',         highlight: true },
      { label:'Applicants',  a: String(a.applicants||'—'),         b: String(b.applicants||'—')         },
    ];
    return (
      <Modal visible={compareModal} animationType="slide" transparent onRequestClose={()=>setCompareModal(false)}>
        <View style={s.filterOverlay}>
          <View style={[s.filterSheet, { maxHeight: height*0.88 }]}>
            <View style={s.filterHeader}>
              <Text style={s.filterTitle}>Compare Jobs</Text>
              <TouchableOpacity onPress={()=>setCompareModal(false)}><Ionicons name="close" size={24} color="#1e293b" /></TouchableOpacity>
            </View>
            {/* Headers */}
            <View style={s.compareHeader}>
              <View style={s.compareHeaderItem}>
                <Text style={s.compareEmoji}>{getEmoji(a.title)}</Text>
                <Text style={s.compareJobTitle} numberOfLines={2}>{a.title}</Text>
                <Text style={s.compareCompany}>{a.company||a.department}</Text>
              </View>
              <View style={s.compareVs}><Text style={s.compareVsText}>VS</Text></View>
              <View style={s.compareHeaderItem}>
                <Text style={s.compareEmoji}>{getEmoji(b.title)}</Text>
                <Text style={s.compareJobTitle} numberOfLines={2}>{b.title}</Text>
                <Text style={s.compareCompany}>{b.company||b.department}</Text>
              </View>
            </View>
            <ScrollView>
              {rows.map((row,i) => (
                <View key={i} style={[s.compareRow, i%2===1 && { backgroundColor:'#F9FAFB' }]}>
                  <Text style={[s.compareCell, { color: row.highlight ? matchColor(msA||0) : '#1e293b' }]}>{row.a}</Text>
                  <Text style={s.compareRowLabel}>{row.label}</Text>
                  <Text style={[s.compareCell, { color: row.highlight ? matchColor(msB||0) : '#1e293b' }]}>{row.b}</Text>
                </View>
              ))}
            </ScrollView>
            <View style={s.filterFooter}>
              <TouchableOpacity style={s.filterResetBtn} onPress={()=>{setCompareJobs([]);setCompareMode(false);setCompareModal(false);}}>
                <Text style={s.filterResetText}>Clear</Text>
              </TouchableOpacity>
              <TouchableOpacity style={s.filterApplyBtn} onPress={()=>{setCompareModal(false);openJob(msA>=msB?a:b);}}>
                <Text style={s.filterApplyText}>Apply to Best Match</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  // ─── Prep Modal ────────────────────────────────────────────────────────────
  const renderPrepModal = () => (
    <Modal visible={prepModal} animationType="slide" transparent onRequestClose={()=>setPrepModal(false)}>
      <View style={s.filterOverlay}>
        <View style={[s.filterSheet, { maxHeight: height*0.85 }]}>
          <View style={s.filterHeader}>
            <Text style={s.filterTitle}>🎯 Interview Prep</Text>
            <TouchableOpacity onPress={()=>setPrepModal(false)}><Ionicons name="close" size={24} color="#1e293b" /></TouchableOpacity>
          </View>
          {generatingPrep
            ? <View style={{ padding:40, alignItems:'center' }}>
                <ActivityIndicator size={36} color="#0e4e96" />
                <Text style={{ marginTop:12, color:'#64748b' }}>Generating questions…</Text>
              </View>
            : <FlatList
                data={prepQuestions}
                keyExtractor={(_,i)=>String(i)}
                contentContainerStyle={{ padding:16, paddingBottom:40 }}
                renderItem={({ item, index }) => (
                  <View style={s.prepRow}>
                    <View style={s.prepNum}><Text style={s.prepNumText}>{index+1}</Text></View>
                    <Text style={s.prepQ}>{item}</Text>
                  </View>
                )}
              />
          }
        </View>
      </View>
    </Modal>
  );

  // ─── Note Modal ────────────────────────────────────────────────────────────
  const renderNoteModal = () => (
    <Modal visible={noteModal} animationType="slide" transparent onRequestClose={()=>setNoteModal(false)}>
      <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'} style={s.filterOverlay}>
        <View style={[s.filterSheet, { maxHeight:380 }]}>
          <View style={s.filterHeader}>
            <Text style={s.filterTitle}>📝 My Note</Text>
            <TouchableOpacity onPress={()=>setNoteModal(false)}><Ionicons name="close" size={24} color="#1e293b" /></TouchableOpacity>
          </View>
          <TextInput style={[s.field,{height:120,margin:16}]} multiline placeholder="Add your personal notes about this job…" placeholderTextColor="#BBB"
            value={noteDraft} onChangeText={setNoteDraft} />
          <View style={s.filterFooter}>
            <TouchableOpacity style={s.filterResetBtn} onPress={()=>setNoteModal(false)}>
              <Text style={s.filterResetText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.filterApplyBtn} onPress={saveNote}>
              <Text style={s.filterApplyText}>Save Note</Text>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );

  // ─── Salary Modal ──────────────────────────────────────────────────────────
  const renderSalaryModal = () => (
    <Modal visible={salaryModal} animationType="fade" transparent onRequestClose={()=>setSalaryModal(false)}>
      <TouchableOpacity style={s.filterOverlay} activeOpacity={1} onPress={()=>setSalaryModal(false)}>
        <View style={[s.filterSheet, { maxHeight:420 }]}>
          <View style={s.filterHeader}>
            <Text style={s.filterTitle}>💷 Salary Insights</Text>
            <TouchableOpacity onPress={()=>setSalaryModal(false)}><Ionicons name="close" size={24} color="#1e293b" /></TouchableOpacity>
          </View>
          {selectedJob && (() => {
            const sal = parseSalaryNum(selectedJob) || 60000;
            const ranges = [
              { label:'Entry',  pct:62, val: Math.round(sal*0.62) },
              { label:'Mid',    pct:82, val: Math.round(sal*0.82) },
              { label:'Senior', pct:100,val: sal                   },
              { label:'Lead',   pct:120,val: Math.round(sal*1.20) },
            ];
            return (
              <View style={{ padding:16 }}>
                <Text style={s.salaryTitle}>{selectedJob.title} — UK Market Rates</Text>
                {ranges.map(r => (
                  <View key={r.label} style={{ marginBottom:12 }}>
                    <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:4 }}>
                      <Text style={s.salaryRangeLabel}>{r.label}</Text>
                      <Text style={s.salaryRangeVal}>£{r.val.toLocaleString()}</Text>
                    </View>
                    <View style={s.salaryBar}>
                      <View style={[s.salaryBarFill, { width:`${Math.min(r.pct,100)}%` }]} />
                    </View>
                  </View>
                ))}
                <Text style={s.salaryNote}>* Estimates based on UK engineering market data</Text>
              </View>
            );
          })()}
        </View>
      </TouchableOpacity>
    </Modal>
  );

  // ─── Report Modal ──────────────────────────────────────────────────────────
  const renderReportModal = () => (
    <Modal visible={reportModal} animationType="fade" transparent onRequestClose={()=>setReportModal(false)}>
      <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'} style={s.filterOverlay}>
        <View style={[s.filterSheet, { maxHeight:400 }]}>
          <View style={s.filterHeader}>
            <Text style={s.filterTitle}>🚩 Report Job</Text>
            <TouchableOpacity onPress={()=>setReportModal(false)}><Ionicons name="close" size={24} color="#1e293b" /></TouchableOpacity>
          </View>
          <View style={{ padding:16 }}>
            <Text style={s.fieldLabel}>Reason</Text>
            <View style={s.filterGrid}>
              {['Misleading information','Fake/spam job','Inappropriate content','Already filled','Salary not as described','Other'].map(r => (
                <TouchableOpacity key={r} style={[s.filterChip, reportReason===r && s.filterChipActive]} onPress={()=>setReportReason(r)}>
                  <Text style={[s.filterChipText, reportReason===r && s.filterChipTextActive]}>{r}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          <View style={s.filterFooter}>
            <TouchableOpacity style={s.filterResetBtn} onPress={()=>setReportModal(false)}>
              <Text style={s.filterResetText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[s.filterApplyBtn,{backgroundColor:'#EF4444'}]} onPress={submitReport}>
              <Text style={s.filterApplyText}>Submit Report</Text>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );

  // ─── Main render ───────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={s.root}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={s.header}>
        <TouchableOpacity style={s.headerBack} onPress={()=>navigation.navigate('ProfessionalDashboard')}>
          <Ionicons name="arrow-back" size={24} color="#FFF" />
        </TouchableOpacity>
        <View style={s.headerCenter}>
          <JobengLogo />
          <Text style={s.headerTitle}>Browse Jobs</Text>
        </View>
        <View style={{ flexDirection:'row', gap:8 }}>
          {/* Compare mode toggle */}
          <TouchableOpacity style={[s.headerIconBtn, compareMode && s.headerIconBtnActive]} onPress={()=>{setCompareMode(!compareMode);if(compareMode){setCompareJobs([]);}}}>
            <MaterialCommunityIcons name="compare" size={20} color={compareMode?'#F59E0B':'#FFF'} />
          </TouchableOpacity>
          <TouchableOpacity style={[s.headerIconBtn, filterCount>0 && s.headerIconBtnActive]} onPress={()=>setFilterModal(true)}>
            <Ionicons name="options-outline" size={20} color="#FFF" />
            {filterCount > 0 && <View style={s.filterBadge}><Text style={s.filterBadgeText}>{filterCount}</Text></View>}
          </TouchableOpacity>
        </View>
      </View>

      {/* Search bar */}
      <View style={s.searchBar}>
        <View style={s.searchInput}>
          <Ionicons name="search" size={18} color="#94a3b8" style={{ marginLeft:12 }} />
          <TextInput
            ref={searchRef}
            style={s.searchText}
            placeholder="Search jobs, skills, companies…"
            placeholderTextColor="#94a3b8"
            value={searchQuery}
            onChangeText={handleSearch}
            returnKeyType="search"
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={()=>setSearchQuery('')} style={{ padding:8 }}>
              <Ionicons name="close-circle" size={18} color="#94a3b8" />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Tab bar */}
      <View style={s.tabBar}>
        {TABS.map(t => {
          const badgeCount = t.id==='saved'?savedJobsData.length:t.id==='applied'?appliedJobs.length:t.id==='alerts'?alerts.length:0;
          return (
            <TouchableOpacity key={t.id} style={[s.tabItem, activeTab===t.id && s.tabItemActive]} onPress={()=>setActiveTab(t.id)}>
              <MaterialCommunityIcons name={t.icon} size={17} color={activeTab===t.id?'#0e4e96':'#94a3b8'} />
              <Text style={[s.tabText, activeTab===t.id && s.tabTextActive]}>{t.label}</Text>
              {badgeCount > 0 && <View style={s.tabBadge}><Text style={s.tabBadgeText}>{badgeCount}</Text></View>}
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Category chips (only on Browse tab) */}
      {activeTab === 'browse' && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.catBar} contentContainerStyle={{ paddingHorizontal:12, gap:8 }}>
          {JOB_CATEGORIES.map(cat => (
            <TouchableOpacity key={cat.id} style={[s.catChip, selectedCategory===cat.id && s.catChipActive]} onPress={()=>setSelectedCategory(cat.id)}>
              <Ionicons name={cat.icon} size={13} color={selectedCategory===cat.id?'#FFF':'#64748b'} />
              <Text style={[s.catText, selectedCategory===cat.id && s.catTextActive]}>{cat.name}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}

      {/* Tab content */}
      <View style={{ flex:1 }}>
        {activeTab==='browse'  && renderBrowse()}
        {activeTab==='saved'   && renderSaved()}
        {activeTab==='applied' && renderApplied()}
        {activeTab==='alerts'  && renderAlerts()}
      </View>

      {/* Compare FAB */}
      {compareMode && compareJobs.length===2 && (
        <TouchableOpacity style={s.compareFAB} onPress={()=>setCompareModal(true)}>
          <MaterialCommunityIcons name="compare" size={20} color="#FFF" />
          <Text style={s.compareFABText}>Compare Now</Text>
        </TouchableOpacity>
      )}

      {/* Bottom nav */}
      <View style={s.bottomNav}>
        {[
          { label:'Dashboard', icon:'speedometer-outline',   route:'ProfessionalDashboard' },
          { label:'Services',  icon:'apps-outline',          route:'Services'              },
          { label:'Profile',   icon:'person-outline',        route:'Profile'               },
          { label:'Search',    icon:'search',                active: true                  },
          { label:'Submit',    icon:'paper-plane-outline',   route:'SubmitForJobs'         },
        ].map((it,i) => (
          <TouchableOpacity key={i} style={s.navItem} onPress={()=>it.route&&navigation.navigate(it.route)}>
            <Ionicons name={it.icon} size={24} color={it.active?'#0e4e96':'#64748b'} />
            <Text style={[s.navText, it.active && s.navTextActive]}>{it.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Modals */}
      {renderJobModal()}
      {renderFilterModal()}
      {renderAlertModal()}
      {renderCompareModal()}
      {renderPrepModal()}
      {renderNoteModal()}
      {renderSalaryModal()}
      {renderReportModal()}
    </SafeAreaView>
  );
};

// ─── Mini components ─────────────────────────────────────────────────────────
const FilterChip = ({ label, onRemove }) => (
  <TouchableOpacity style={st.fChip} onPress={onRemove}>
    <Text style={st.fChipText}>{label}</Text>
    <Ionicons name="close" size={11} color="#0e4e96" />
  </TouchableOpacity>
);
const st = StyleSheet.create({
  fChip:     { flexDirection:'row', alignItems:'center', gap:4, backgroundColor:'#EFF6FF', borderWidth:1, borderColor:'#BFDBFE', paddingHorizontal:10, paddingVertical:5, borderRadius:16, marginRight:8 },
  fChipText: { fontSize:12, color:'#0e4e96', fontWeight:'500' },
});

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  root:         { flex:1, backgroundColor:'#F8FAFC' },
  // Header
  header:       { flexDirection:'row', alignItems:'center', backgroundColor:'#0e4e96', paddingVertical:12, paddingHorizontal:16 },
  headerBack:   { padding:4 },
  headerCenter: { flex:1, flexDirection:'row', alignItems:'center', marginLeft:10 },
  headerTitle:  { fontSize:18, fontWeight:'700', color:'#FFF', marginLeft:8 },
  headerIconBtn:{ padding:6, borderRadius:8, backgroundColor:'rgba(255,255,255,0.15)' },
  headerIconBtnActive:{ backgroundColor:'rgba(255,255,255,0.25)' },
  filterBadge:  { position:'absolute', top:-4, right:-4, backgroundColor:'#F59E0B', borderRadius:8, width:16, height:16, justifyContent:'center', alignItems:'center' },
  filterBadgeText:{ fontSize:9, color:'#FFF', fontWeight:'700' },
  // Search
  searchBar:    { backgroundColor:'#FFF', paddingHorizontal:12, paddingVertical:8, borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  searchInput:  { flexDirection:'row', alignItems:'center', backgroundColor:'#F1F5F9', borderRadius:10 },
  searchText:   { flex:1, height:40, fontSize:14, color:'#1e293b', paddingHorizontal:8 },
  // Tabs
  tabBar:       { flexDirection:'row', backgroundColor:'#FFF', borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  tabItem:      { flex:1, alignItems:'center', paddingVertical:10, borderBottomWidth:2, borderBottomColor:'transparent', gap:2 },
  tabItemActive:{ borderBottomColor:'#0e4e96' },
  tabText:      { fontSize:11, color:'#94a3b8', fontWeight:'500' },
  tabTextActive:{ color:'#0e4e96', fontWeight:'700' },
  tabBadge:     { position:'absolute', top:4, right:8, backgroundColor:'#EF4444', borderRadius:8, paddingHorizontal:4, paddingVertical:1 },
  tabBadgeText: { fontSize:9, color:'#FFF', fontWeight:'700' },
  // Category bar
  catBar:       { backgroundColor:'#FFF', paddingVertical:8, borderBottomWidth:1, borderBottomColor:'#E2E8F0', flexGrow:0 },
  catChip:      { flexDirection:'row', alignItems:'center', gap:4, paddingHorizontal:12, paddingVertical:6, borderRadius:20, backgroundColor:'#F1F5F9' },
  catChipActive:{ backgroundColor:'#0e4e96' },
  catText:      { fontSize:12, color:'#64748b', fontWeight:'500' },
  catTextActive:{ color:'#FFF' },
  // Recent searches
  recentRow:    { flexDirection:'row', alignItems:'center', paddingHorizontal:16, paddingVertical:8, backgroundColor:'#FFF', borderBottomWidth:1, borderBottomColor:'#F1F5F9', gap:8 },
  recentLabel:  { fontSize:12, color:'#94a3b8', fontWeight:'600' },
  recentChip:   { flexDirection:'row', alignItems:'center', gap:4, backgroundColor:'#F8FAFC', borderWidth:1, borderColor:'#E2E8F0', paddingHorizontal:10, paddingVertical:4, borderRadius:14 },
  recentChipText:{ fontSize:12, color:'#475569' },
  // Recently viewed
  rvSection:    { backgroundColor:'#FFF', paddingVertical:12, borderBottomWidth:1, borderBottomColor:'#F1F5F9' },
  rvTitle:      { fontSize:13, fontWeight:'700', color:'#1e293b', paddingHorizontal:16, marginBottom:8 },
  rvCard:       { width:130, backgroundColor:'#F8FAFC', borderRadius:10, padding:10, marginLeft:12, borderWidth:1, borderColor:'#E2E8F0' },
  rvEmoji:      { fontSize:24, textAlign:'center', marginBottom:4 },
  rvJobTitle:   { fontSize:12, fontWeight:'600', color:'#1e293b', textAlign:'center' },
  rvSalary:     { fontSize:11, color:'#64748b', textAlign:'center', marginTop:2 },
  // Stats bar
  statsBar:     { flexDirection:'row', justifyContent:'space-between', alignItems:'center', paddingHorizontal:16, paddingVertical:8, backgroundColor:'#FFF', borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  statsText:    { fontSize:13, color:'#64748b' },
  statsRight:   { flexDirection:'row', alignItems:'center', gap:10 },
  compareBtn:   { flexDirection:'row', alignItems:'center', backgroundColor:'#0e4e96', borderRadius:8, paddingHorizontal:10, paddingVertical:5, gap:4 },
  compareBtnText:{ fontSize:12, color:'#FFF', fontWeight:'600' },
  viewModeBtn:  { padding:4 },
  activeFiltersBar:{ backgroundColor:'#FFF', paddingVertical:8, borderBottomWidth:1, borderBottomColor:'#E2E8F0', flexGrow:0 },
  clearAllBtn:  { paddingHorizontal:8, paddingVertical:4 },
  clearAllText: { fontSize:12, color:'#EF4444', fontWeight:'600' },
  // Job card (list)
  jobCard:      { backgroundColor:'#FFF', borderRadius:14, padding:14, marginBottom:12, shadowColor:'#000', shadowOffset:{width:0,height:2}, shadowOpacity:0.06, shadowRadius:8, elevation:3 },
  jobCardApplied:{ borderLeftWidth:3, borderLeftColor:'#10B981' },
  jobCardUrgent:{ borderLeftWidth:3, borderLeftColor:'#EF4444' },
  jobCardCompare:{ borderWidth:2, borderColor:'#0e4e96' },
  badgeRow:     { flexDirection:'row', alignItems:'center', marginBottom:10, gap:6, flexWrap:'wrap' },
  newBadge:     { backgroundColor:'#10B981', paddingHorizontal:7, paddingVertical:2, borderRadius:6 },
  newBadgeText: { fontSize:9, color:'#FFF', fontWeight:'700' },
  urgentBadge:  { flexDirection:'row', alignItems:'center', gap:2, backgroundColor:'#EF4444', paddingHorizontal:7, paddingVertical:2, borderRadius:6 },
  urgentBadgeText:{ fontSize:9, color:'#FFF', fontWeight:'700' },
  appliedBadge: { backgroundColor:'#F0FDF4', borderWidth:1, borderColor:'#86EFAC', paddingHorizontal:7, paddingVertical:2, borderRadius:6 },
  appliedBadgeText:{ fontSize:9, color:'#10B981', fontWeight:'600' },
  matchBadge:   { paddingHorizontal:7, paddingVertical:2, borderRadius:6 },
  matchBadgeText:{ fontSize:9, color:'#FFF', fontWeight:'700' },
  compareBadge: { backgroundColor:'#EEF2FF', borderWidth:1, borderColor:'#C7D2FE', paddingHorizontal:7, paddingVertical:2, borderRadius:6 },
  compareBadgeText:{ fontSize:9, color:'#0e4e96', fontWeight:'600' },
  saveBtn:      { marginLeft:'auto' },
  cardHeader:   { flexDirection:'row', alignItems:'center', marginBottom:10 },
  logoBox:      { width:44, height:44, borderRadius:10, backgroundColor:'#F1F5F9', justifyContent:'center', alignItems:'center', marginRight:12 },
  logoEmoji:    { fontSize:22 },
  cardTitle:    { fontSize:15, fontWeight:'700', color:'#1e293b', marginBottom:2 },
  cardCompany:  { fontSize:13, color:'#64748b' },
  metaGrid:     { marginBottom:10 },
  metaRow:      { flexDirection:'row', alignItems:'center', marginBottom:4, gap:6 },
  metaText:     { fontSize:12, color:'#64748b' },
  skillsRow:    { marginBottom:10 },
  skillChip:    { backgroundColor:'#F1F5F9', paddingHorizontal:9, paddingVertical:3, borderRadius:6, marginRight:6 },
  skillChipMatch:{ backgroundColor:'#F0FDF4', borderWidth:1, borderColor:'#86EFAC' },
  skillChipText:{ fontSize:11, color:'#475569' },
  skillChipTextMatch:{ color:'#10B981', fontWeight:'500' },
  deadlineStrip:{ flexDirection:'row', alignItems:'center', gap:6, borderRadius:8, paddingHorizontal:10, paddingVertical:6, marginBottom:10 },
  deadlineText: { fontSize:12, fontWeight:'600' },
  noteStrip:    { flexDirection:'row', alignItems:'center', gap:6, backgroundColor:'#FFFBEB', borderRadius:8, paddingHorizontal:10, paddingVertical:5, marginBottom:10 },
  noteStripText:{ fontSize:11, color:'#92400E', flex:1 },
  cardActions:  { flexDirection:'row', gap:10 },
  detailsBtn:   { flex:1, backgroundColor:'#F8FAFC', borderWidth:1, borderColor:'#E2E8F0', paddingVertical:10, borderRadius:10, alignItems:'center' },
  detailsBtnText:{ fontSize:13, color:'#334155', fontWeight:'600' },
  applyBtn:     { flex:1, backgroundColor:'#0e4e96', paddingVertical:10, borderRadius:10, alignItems:'center' },
  applyBtnApplied:{ backgroundColor:'#10B981' },
  applyBtnText: { fontSize:13, color:'#FFF', fontWeight:'700' },
  // Grid card
  gridCard:     { width: CARD_W, backgroundColor:'#FFF', borderRadius:12, padding:12, shadowColor:'#000', shadowOffset:{width:0,height:2}, shadowOpacity:0.06, shadowRadius:6, elevation:2 },
  gridCardSelected:{ borderWidth:2, borderColor:'#0e4e96' },
  gridCardUrgent:{ borderLeftWidth:3, borderLeftColor:'#EF4444' },
  gridLogoBox:  { width:44, height:44, borderRadius:10, backgroundColor:'#F1F5F9', justifyContent:'center', alignItems:'center', marginBottom:8 },
  gridEmoji:    { fontSize:22 },
  gridTitle:    { fontSize:13, fontWeight:'700', color:'#1e293b', marginBottom:2 },
  gridCompany:  { fontSize:12, color:'#64748b', marginBottom:4 },
  gridSalary:   { fontSize:12, color:'#0e4e96', fontWeight:'600', marginBottom:8 },
  gridFooter:   { flexDirection:'row', alignItems:'center', justifyContent:'space-between' },
  gridMatchBadge:{ paddingHorizontal:6, paddingVertical:2, borderRadius:6 },
  gridMatchText:{ fontSize:10, fontWeight:'700' },
  gridSaveBtn:  {},
  gridAppliedBadge:{ position:'absolute', top:8, right:8, backgroundColor:'#10B981', borderRadius:8, width:18, height:18, justifyContent:'center', alignItems:'center' },
  gridAppliedText:{ fontSize:10, color:'#FFF', fontWeight:'700' },
  gridCompareBadge:{ position:'absolute', top:0, left:0, right:0, height:3, backgroundColor:'#0e4e96', borderTopLeftRadius:12, borderTopRightRadius:12 },
  gridCompareText:{ fontSize:0 },
  // Loading / empty
  loadingBox:   { flex:1, justifyContent:'center', alignItems:'center', paddingTop:80 },
  loadingText:  { marginTop:12, fontSize:16, color:'#64748b' },
  emptyBox:     { flex:1, justifyContent:'center', alignItems:'center', paddingHorizontal:32, paddingTop:60 },
  emptyTitle:   { fontSize:20, fontWeight:'700', color:'#1e293b', marginTop:16, marginBottom:8 },
  emptySubtitle:{ fontSize:14, color:'#94a3b8', textAlign:'center', marginBottom:20 },
  emptyResetBtn:{ backgroundColor:'#0e4e96', paddingVertical:12, paddingHorizontal:24, borderRadius:10 },
  emptyResetText:{ fontSize:14, color:'#FFF', fontWeight:'600' },
  // Tab header
  tabHeaderBar: { flexDirection:'row', justifyContent:'space-between', alignItems:'center', paddingHorizontal:16, paddingVertical:12, backgroundColor:'#FFF', borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  tabHeaderTitle:{ fontSize:16, fontWeight:'700', color:'#1e293b' },
  tabHeaderAction:{ fontSize:13, color:'#EF4444', fontWeight:'600' },
  // Applied card
  appCard:      { backgroundColor:'#FFF', borderRadius:14, padding:14, marginBottom:12, shadowColor:'#000', shadowOffset:{width:0,height:2}, shadowOpacity:0.06, shadowRadius:6, elevation:2 },
  appCardHeader:{ flexDirection:'row', alignItems:'flex-start', marginBottom:14 },
  appJobTitle:  { fontSize:15, fontWeight:'700', color:'#1e293b', marginBottom:2 },
  appCompany:   { fontSize:13, color:'#64748b', marginBottom:2 },
  appDate:      { fontSize:11, color:'#94a3b8' },
  statusBadge:  { flexDirection:'row', alignItems:'center', gap:4, borderRadius:8, paddingHorizontal:8, paddingVertical:5 },
  statusBadgeText:{ fontSize:10, fontWeight:'600' },
  progressTracker:{ flexDirection:'row', alignItems:'flex-start', justifyContent:'space-between' },
  progressStep: { alignItems:'center', flex:1 },
  progressDot:  { width:20, height:20, borderRadius:10, justifyContent:'center', alignItems:'center' },
  progressDotInner:{ width:8, height:8, borderRadius:4, backgroundColor:'#FFF' },
  progressLine: { position:'absolute', top:10, left:'50%', right:'-50%', height:2, zIndex:-1 },
  progressLabel:{ fontSize:8, color:'#94a3b8', marginTop:4, textAlign:'center' },
  feedbackBox:  { flexDirection:'row', alignItems:'flex-start', gap:8, backgroundColor:'#F8FAFC', borderRadius:8, padding:10, marginTop:10 },
  feedbackText: { flex:1, fontSize:12, color:'#64748b', lineHeight:18 },
  // Alerts
  addAlertBtn:  { flexDirection:'row', alignItems:'center', backgroundColor:'#0e4e96', borderRadius:8, paddingHorizontal:12, paddingVertical:7, gap:4 },
  addAlertText: { fontSize:13, color:'#FFF', fontWeight:'600' },
  alertCard:    { backgroundColor:'#FFF', borderRadius:14, padding:14, marginBottom:12, shadowColor:'#000', shadowOffset:{width:0,height:2}, shadowOpacity:0.05, shadowRadius:6, elevation:2 },
  alertCardHeader:{ flexDirection:'row', alignItems:'center' },
  alertIcon:    { width:40, height:40, borderRadius:20, justifyContent:'center', alignItems:'center' },
  alertTitle:   { fontSize:14, fontWeight:'700', color:'#1e293b' },
  alertMeta:    { fontSize:12, color:'#64748b', marginTop:2 },
  alertFreq:    { fontSize:11, color:'#94a3b8', marginTop:2 },
  alertCardFooter:{ flexDirection:'row', alignItems:'center', marginTop:12, paddingTop:10, borderTopWidth:1, borderTopColor:'#F1F5F9', gap:12 },
  alertFooterText:{ flex:1, fontSize:11, color:'#94a3b8' },
  alertEditBtn: { flexDirection:'row', alignItems:'center', gap:4 },
  alertEditText:{ fontSize:12, fontWeight:'600' },
  // Job detail modal
  modalOverlay: { flex:1, backgroundColor:'rgba(0,0,0,0.5)', justifyContent:'flex-end' },
  modalSheet:   { backgroundColor:'#FFF', borderTopLeftRadius:24, borderTopRightRadius:24, overflow:'hidden' },
  modalHeader:  { flexDirection:'row', alignItems:'center', paddingHorizontal:16, paddingVertical:14, borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  modalBackBtn: { padding:4 },
  modalHeaderTitle:{ flex:1, fontSize:16, fontWeight:'700', color:'#1e293b', marginHorizontal:12 },
  jobHero:      { alignItems:'center', paddingVertical:24, paddingHorizontal:20, borderBottomWidth:1, borderBottomColor:'#F1F5F9' },
  heroLogoBox:  { width:72, height:72, borderRadius:16, backgroundColor:'#F1F5F9', justifyContent:'center', alignItems:'center', marginBottom:12 },
  heroJobTitle: { fontSize:22, fontWeight:'800', color:'#1e293b', textAlign:'center', marginBottom:4 },
  heroCompany:  { fontSize:16, color:'#64748b', marginBottom:6 },
  heroLocationRow:{ flexDirection:'row', alignItems:'center', gap:4 },
  heroLocation: { fontSize:14, color:'#64748b' },
  socialProof:  { flexDirection:'row', gap:16, marginTop:8 },
  socialText:   { fontSize:12, color:'#94a3b8' },
  matchBox:     { margin:16, borderRadius:12, borderWidth:1, padding:14, backgroundColor:'#F8FAFC' },
  matchBoxTitle:{ fontSize:14, fontWeight:'700', color:'#1e293b' },
  matchBoxScore:{ fontSize:18, fontWeight:'bold' },
  matchBar:     { height:8, backgroundColor:'#E2E8F0', borderRadius:4, overflow:'hidden' },
  matchBarFill: { height:8, borderRadius:4 },
  matchBoxSub:  { fontSize:12, color:'#64748b', marginTop:8, lineHeight:18 },
  statsRow:     { flexDirection:'row', borderTopWidth:1, borderBottomWidth:1, borderColor:'#F1F5F9' },
  statItem:     { flex:1, alignItems:'center', paddingVertical:14, gap:4, borderRightWidth:1, borderRightColor:'#F1F5F9' },
  statLabel:    { fontSize:10, color:'#94a3b8', fontWeight:'500' },
  statVal:      { fontSize:12, color:'#1e293b', fontWeight:'700', textAlign:'center' },
  deadlineBanner:{ flexDirection:'row', alignItems:'center', gap:8, marginHorizontal:16, marginTop:12, borderRadius:10, borderWidth:1, paddingHorizontal:14, paddingVertical:10 },
  deadlineBannerText:{ fontSize:13, fontWeight:'600' },
  quickActionsRow:{ flexDirection:'row', paddingHorizontal:16, paddingVertical:14, gap:6, borderBottomWidth:1, borderBottomColor:'#F1F5F9', flexWrap:'wrap' },
  quickActionBtn:{ alignItems:'center', minWidth:58 },
  quickActionIcon:{ width:40, height:40, borderRadius:20, backgroundColor:'#EEF2FF', justifyContent:'center', alignItems:'center', marginBottom:4 },
  quickActionLabel:{ fontSize:10, color:'#374151', textAlign:'center', fontWeight:'500' },
  myNoteBox:    { flexDirection:'row', alignItems:'center', gap:8, marginHorizontal:16, marginVertical:8, backgroundColor:'#FFFBEB', borderRadius:10, padding:12 },
  myNoteText:   { flex:1, fontSize:13, color:'#92400E' },
  section:      { paddingHorizontal:16, paddingTop:20 },
  sectionTitle: { fontSize:16, fontWeight:'700', color:'#1e293b', marginBottom:10 },
  sectionBody:  { fontSize:14, color:'#475569', lineHeight:22 },
  skillsWrap:   { flexDirection:'row', flexWrap:'wrap', gap:8 },
  detailSkill:  { flexDirection:'row', alignItems:'center', backgroundColor:'#F1F5F9', paddingHorizontal:10, paddingVertical:6, borderRadius:8 },
  detailSkillMatch:{ backgroundColor:'#F0FDF4', borderWidth:1, borderColor:'#86EFAC' },
  detailSkillText:{ fontSize:13, color:'#475569', fontWeight:'500' },
  detailSkillTextMatch:{ color:'#10B981' },
  skillsGap:    { fontSize:12, color:'#64748b', marginTop:10, fontStyle:'italic' },
  bulletRow:    { flexDirection:'row', alignItems:'flex-start', gap:8, marginBottom:8 },
  bullet:       { width:6, height:6, borderRadius:3, backgroundColor:'#0e4e96', marginTop:7 },
  bulletText:   { flex:1, fontSize:14, color:'#475569', lineHeight:22 },
  benefitsGrid: { flexDirection:'row', flexWrap:'wrap', gap:10 },
  benefitItem:  { flexDirection:'row', alignItems:'center', gap:6, width:'48%' },
  benefitText:  { fontSize:13, color:'#475569', flex:1 },
  companyInfoCard:{ flexDirection:'row', alignItems:'center', backgroundColor:'#F8FAFC', borderRadius:10, padding:12 },
  companyInfoEmoji:{ fontSize:28 },
  companyInfoName:{ fontSize:15, fontWeight:'700', color:'#1e293b' },
  companyInfoMeta:{ fontSize:12, color:'#64748b', marginTop:2 },
  similarJobRow:{ flexDirection:'row', alignItems:'center', backgroundColor:'#F8FAFC', borderRadius:10, padding:12, marginBottom:8 },
  similarLogo:  { width:36, height:36, borderRadius:8, backgroundColor:'#E2E8F0', justifyContent:'center', alignItems:'center' },
  similarTitle: { fontSize:14, fontWeight:'600', color:'#1e293b' },
  similarMeta:  { fontSize:12, color:'#94a3b8', marginTop:2 },
  modalCTA:     { flexDirection:'row', gap:10, padding:16, borderTopWidth:1, borderTopColor:'#E2E8F0', backgroundColor:'#FFF' },
  modalSaveBtn: { width:46, height:46, borderRadius:12, borderWidth:1, borderColor:'#E2E8F0', justifyContent:'center', alignItems:'center' },
  modalShareBtn:{ width:46, height:46, borderRadius:12, borderWidth:1, borderColor:'#E2E8F0', justifyContent:'center', alignItems:'center' },
  modalApplyBtn:{ flex:1, backgroundColor:'#0e4e96', borderRadius:12, justifyContent:'center', alignItems:'center', height:46 },
  modalApplyBtnApplied:{ backgroundColor:'#10B981' },
  modalApplyText:{ fontSize:15, color:'#FFF', fontWeight:'700' },
  // Filter modal
  filterOverlay:{ flex:1, backgroundColor:'rgba(0,0,0,0.45)', justifyContent:'flex-end' },
  filterSheet:  { backgroundColor:'#FFF', borderTopLeftRadius:24, borderTopRightRadius:24, maxHeight: height*0.88, paddingBottom: Platform.OS==='ios'?34:16 },
  filterHeader: { flexDirection:'row', justifyContent:'space-between', alignItems:'center', paddingHorizontal:20, paddingVertical:16, borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  filterTitle:  { fontSize:18, fontWeight:'700', color:'#1e293b' },
  filterSection:{ fontSize:13, fontWeight:'700', color:'#1e293b', paddingHorizontal:20, paddingTop:16, paddingBottom:8 },
  filterGrid:   { flexDirection:'row', flexWrap:'wrap', paddingHorizontal:16, gap:8 },
  filterChip:   { paddingHorizontal:13, paddingVertical:7, borderRadius:20, backgroundColor:'#F1F5F9', borderWidth:1, borderColor:'#E2E8F0' },
  filterChipActive:{ backgroundColor:'#0e4e96', borderColor:'#0e4e96' },
  filterChipText:{ fontSize:12, color:'#64748b', fontWeight:'500' },
  filterChipTextActive:{ color:'#FFF' },
  filterToggleRow:{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginHorizontal:20, marginTop:16, paddingVertical:12, borderTopWidth:1, borderTopColor:'#F1F5F9' },
  filterToggleLabel:{ fontSize:14, fontWeight:'600', color:'#1e293b' },
  filterToggleSub:{ fontSize:12, color:'#94a3b8', marginTop:1 },
  filterFooter: { flexDirection:'row', gap:12, paddingHorizontal:20, paddingTop:14, paddingBottom:4 },
  filterResetBtn:{ flex:1, paddingVertical:13, borderRadius:12, borderWidth:1, borderColor:'#E2E8F0', alignItems:'center' },
  filterResetText:{ fontSize:14, color:'#64748b', fontWeight:'600' },
  filterApplyBtn:{ flex:2, paddingVertical:13, borderRadius:12, backgroundColor:'#0e4e96', alignItems:'center' },
  filterApplyText:{ fontSize:14, color:'#FFF', fontWeight:'700' },
  // Form
  fieldLabel:   { fontSize:13, fontWeight:'600', color:'#374151', marginBottom:6, paddingHorizontal:16 },
  field:        { backgroundColor:'#F9FAFB', borderWidth:1, borderColor:'#E5E7EB', borderRadius:10, paddingHorizontal:14, paddingVertical:12, fontSize:14, color:'#111', marginBottom:10, marginHorizontal:16 },
  // Compare
  compareHeader:{ flexDirection:'row', padding:16, alignItems:'center', borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  compareHeaderItem:{ flex:1, alignItems:'center', gap:6 },
  compareEmoji: { fontSize:32 },
  compareJobTitle:{ fontSize:14, fontWeight:'700', color:'#1e293b', textAlign:'center' },
  compareCompany:{ fontSize:12, color:'#64748b', textAlign:'center' },
  compareVs:    { width:32, height:32, borderRadius:16, backgroundColor:'#F1F5F9', justifyContent:'center', alignItems:'center', marginHorizontal:8 },
  compareVsText:{ fontSize:12, fontWeight:'900', color:'#64748b' },
  compareRow:   { flexDirection:'row', alignItems:'center', paddingVertical:10, paddingHorizontal:12 },
  compareCell:  { flex:1, fontSize:13, fontWeight:'600', textAlign:'center', color:'#1e293b' },
  compareRowLabel:{ width:80, fontSize:11, color:'#94a3b8', fontWeight:'600', textAlign:'center' },
  // Interview prep
  prepRow:      { flexDirection:'row', alignItems:'flex-start', gap:12, backgroundColor:'#F8FAFC', borderRadius:10, padding:14, marginBottom:10 },
  prepNum:      { width:28, height:28, borderRadius:14, backgroundColor:'#0e4e96', justifyContent:'center', alignItems:'center', flexShrink:0 },
  prepNumText:  { color:'#FFF', fontSize:12, fontWeight:'bold' },
  prepQ:        { flex:1, fontSize:14, color:'#1e293b', lineHeight:22 },
  // Salary modal
  salaryTitle:  { fontSize:15, fontWeight:'700', color:'#1e293b', marginBottom:16 },
  salaryRangeLabel:{ fontSize:13, color:'#6B7280', fontWeight:'500' },
  salaryRangeVal:{ fontSize:13, fontWeight:'700', color:'#0e4e96' },
  salaryBar:    { height:10, backgroundColor:'#E2E8F0', borderRadius:5, overflow:'hidden' },
  salaryBarFill:{ height:10, backgroundColor:'#0e4e96', borderRadius:5 },
  salaryNote:   { fontSize:11, color:'#94a3b8', marginTop:12, fontStyle:'italic' },
  // Compare FAB
  compareFAB:   { position:'absolute', bottom:90, alignSelf:'center', flexDirection:'row', alignItems:'center', backgroundColor:'#0e4e96', borderRadius:24, paddingHorizontal:20, paddingVertical:12, gap:8, elevation:6, shadowColor:'#0e4e96', shadowOffset:{width:0,height:4}, shadowOpacity:0.3, shadowRadius:8 },
  compareFABText:{ color:'#FFF', fontWeight:'700', fontSize:14 },
  // Bottom nav
  bottomNav:    { flexDirection:'row', backgroundColor:'#FFF', borderTopWidth:1, borderTopColor:'#E2E8F0', paddingVertical:8, position:'absolute', bottom:0, left:0, right:0 },
  navItem:      { flex:1, alignItems:'center', paddingVertical:6 },
  navText:      { fontSize:10, color:'#64748b', marginTop:2 },
  navTextActive:{ color:'#0e4e96', fontWeight:'600' },
});

export default BrowseJobsScreen;
